(() => {
  const root = document.documentElement;
  const body = document.body;
  const statusRegion = document.querySelector('[data-theme-status]');
  const desktopHoverMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 990px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const documentMarkupCache = new Map();
  const sectionMarkupCache = new Map();
  const featureInitializers = new Map();
  let animationObserver;

  root.classList.add('theme-ready');
  root.dataset.inputModality = 'pointer';

  document.addEventListener('keydown', (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    root.dataset.inputModality = 'keyboard';
  }, true);

  document.addEventListener('pointerdown', () => {
    root.dataset.inputModality = 'pointer';
  }, true);

  document.addEventListener('touchstart', () => {
    root.dataset.inputModality = 'pointer';
  }, { passive: true, capture: true });

  if (window.Shopify?.designMode) {
    body.dataset.designMode = 'true';
  }

  function collect(scope, selector) {
    const nodes = [];

    if (scope instanceof Element && scope.matches(selector)) {
      nodes.push(scope);
    }

    if (scope.querySelectorAll) {
      nodes.push(...scope.querySelectorAll(selector));
    }

    return nodes;
  }

  function initRegisteredFeatures(scope = document) {
    featureInitializers.forEach((initializer) => {
      initializer(scope);
    });
  }

  function registerFeature(name, initializer) {
    const featureName = String(name || '').trim();

    if (!featureName || typeof initializer !== 'function' || featureInitializers.has(featureName)) {
      return;
    }

    featureInitializers.set(featureName, initializer);

    if (document.readyState !== 'loading') {
      initializer(document);
    }
  }

  function announce(message) {
    if (!statusRegion || !message) return;

    statusRegion.textContent = '';
    window.requestAnimationFrame(() => {
      statusRegion.textContent = message;
    });
  }

  function cloneDocumentNode(node) {
    if (!node) return null;

    if (typeof document.importNode === 'function') {
      return document.importNode(node, true);
    }

    return node.cloneNode(true);
  }

  function syncManagedHead(nextDocument) {
    if (!nextDocument?.head) return;

    const nextManagedNodes = Array.from(nextDocument.head.querySelectorAll('[data-page-meta]'));

    if (nextManagedNodes.length === 0) return;

    collect(document.head, '[data-page-meta]').forEach((node) => {
      node.remove();
    });

    nextManagedNodes.forEach((node) => {
      const clonedNode = cloneDocumentNode(node);

      if (clonedNode) {
        document.head.appendChild(clonedNode);
      }
    });

    if (nextDocument.title) {
      document.title = nextDocument.title;
    }
  }

  function refreshOkendoWidgets(scope) {
    const widgets = collect(scope, '[data-oke-star-rating], [data-oke-widget]');

    if (widgets.length === 0) return;

    const initializeWidgets = () => {
      const api = window.okeWidgetApi;

      if (!api || typeof api.initWidget !== 'function') return false;

      widgets.forEach((widget) => {
        Promise.resolve(api.initWidget(widget, true)).catch(() => {});
      });

      return true;
    };

    if (initializeWidgets()) return;

    document.addEventListener('oke-script-loaded', initializeWidgets, { once: true });
  }

  function formatMoney(cents) {
    const locale = body.dataset.currencyLocale || 'en-US';
    const currencyCode = body.dataset.currencyCode || 'USD';
    const currencyDisplay = body.dataset.currencyDisplay || 'symbol';
    const value = Number(cents || 0) / 100;

    return new Intl.NumberFormat(locale, {
      currency: currencyCode,
      currencyDisplay,
      style: 'currency',
    }).format(value);
  }

  function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      };

      return entities[character] || character;
    });
  }

  function getLocaleRoot() {
    const localeRoot = String(window?.Shopify?.routes?.root || '/');

    return localeRoot.endsWith('/') ? localeRoot : `${localeRoot}/`;
  }

  async function parseJsonResponse(response, {
    invalidResponseMessage = 'Unable to complete this request right now.',
    passwordProtectedMessage = 'This feature is unavailable from password-protected previews.',
  } = {}) {
    const responseUrl = String(response?.url || '');
    const contentType = String(response?.headers?.get('content-type') || '').toLowerCase();
    const isJsonLikeResponse = (
      contentType.includes('json')
      || contentType.includes('javascript')
      || contentType.includes('ecmascript')
    );

    if (response.redirected && /\/password(?:[?#]|$)/.test(responseUrl)) {
      throw {
        code: 'password_protected_preview',
        error: passwordProtectedMessage,
      };
    }

    if (!isJsonLikeResponse) {
      let responseText = '';

      try {
        responseText = await response.text();
      } catch (error) {
        responseText = '';
      }

      if (
        /\/password(?:[?#]|$)/.test(responseUrl)
        || /<title>[^<]*password/i.test(responseText)
      ) {
        throw {
          code: 'password_protected_preview',
          error: passwordProtectedMessage,
        };
      }

      try {
        const data = JSON.parse(responseText);

        if (!response.ok) {
          throw data;
        }

        return data;
      } catch (error) {
        if (error && typeof error === 'object' && !Array.isArray(error) && ('error' in error || 'errors' in error || 'code' in error)) {
          throw error;
        }

        throw {
          code: 'invalid_response',
          error: invalidResponseMessage,
        };
      }
    }

    let data;

    try {
      data = await response.json();
    } catch (error) {
      throw {
        code: 'invalid_json',
        error: invalidResponseMessage,
      };
    }

    if (!response.ok) {
      throw data;
    }

    return data;
  }

  function buildStoreProductUrl(handle) {
    const normalizedHandle = String(handle || '').trim();

    if (!normalizedHandle) return '';

    return `${getLocaleRoot()}products/${encodeURIComponent(normalizedHandle)}`;
  }

  function normalizeStoreProductId(value) {
    const rawValue = String(value ?? '').trim();

    if (!rawValue) return '';
    if (rawValue.startsWith('gid://shopify/Product/')) return rawValue;

    const numericId = rawValue.match(/(\d+)$/)?.[1];

    if (numericId) {
      return `gid://shopify/Product/${numericId}`;
    }

    return rawValue;
  }

  function extractStoreProductImage(product = {}) {
    const imageCandidates = [
      product?.featured_image,
      product?.featuredImage,
      product?.image,
      Array.isArray(product?.images) ? product.images[0] : '',
      product?.featured_media?.preview_image,
      product?.featuredMedia?.previewImage,
    ];

    for (const candidate of imageCandidates) {
      if (!candidate) continue;

      if (typeof candidate === 'string') {
        return {
          imageAlt: String(product?.title || '').trim(),
          imageUrl: candidate,
        };
      }

      const imageUrl = String(candidate?.url || candidate?.src || '').trim();

      if (!imageUrl) continue;

      return {
        imageAlt: String(candidate?.alt || product?.title || '').trim(),
        imageUrl,
      };
    }

    return {
      imageAlt: '',
      imageUrl: '',
    };
  }

  async function loadPredictiveSearchResults({ query, types, limit = 6, signal } = {}) {
    const requestUrl = new URL(`${getLocaleRoot()}search/suggest`, window.location.origin);
    const normalizedQuery = String(query || '').trim();
    const normalizedTypes = 'product';

    requestUrl.searchParams.set('q', normalizedQuery);
    requestUrl.searchParams.set('resources[type]', normalizedTypes);
    // Fetch the full predictive window before deduplicating color siblings.
    requestUrl.searchParams.set('resources[limit]', '10');
    requestUrl.searchParams.set('section_id', 'ks-predictive-search');
    requestUrl.searchParams.set('resources[limit_scope]', 'each');
    requestUrl.searchParams.set('resources[options][unavailable_products]', 'hide');
    requestUrl.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor');

    const response = await fetch(requestUrl.toString(), {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
      },
      signal,
    });

    if (!response.ok) throw new Error('Unable to search right now.');
    const markup = await response.text();
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const fragment = parsed.querySelector('[data-predictive-fragment]');
    if (!fragment) throw new Error('Unable to search right now.');
    const displayLimit = Math.min(10, Math.max(1, Number(limit) || 6));
    const results = {
      products: [...fragment.querySelectorAll('[data-predictive-card]')]
        .slice(0, displayLimit).map((card) => ({ markup: card.outerHTML })),
    };
    ['collections', 'pages', 'articles'].forEach((type) => {
      results[type] = [...fragment.querySelectorAll(`[data-predictive-link="${type}"]`)]
        .slice(0, displayLimit).map((link) => ({ title: link.dataset.title, url: link.getAttribute('href') }));
    });
    return { resources: { results } };
  }

  function renderPredictiveSearchLink(item = {}, typeLabel = '') {
    const title = String(item?.title || '').trim();
    const url = String(item?.url || '').trim();

    if (!title || !url) return '';

    return `
      <a class="ks-header__predictive-link" href="${escapeHtml(url)}">
        <span class="ks-header__predictive-item-title">${escapeHtml(title)}</span>
        ${typeLabel ? `<span class="ks-header__predictive-link-type">${escapeHtml(typeLabel)}</span>` : ''}
      </a>
    `;
  }

  function renderPredictiveSearchResults(data, { query, searchUrl, searchFormTypes } = {}) {
    const resultGroups = data?.resources?.results || {};
    const products = Array.isArray(resultGroups?.products) ? resultGroups.products : [];
    const collections = Array.isArray(resultGroups?.collections) ? resultGroups.collections : [];
    const pages = Array.isArray(resultGroups?.pages) ? resultGroups.pages : [];
    const articles = Array.isArray(resultGroups?.articles) ? resultGroups.articles : [];
    const productMarkup = products.map((product) => product.markup).filter(Boolean).join('');
    const linkMarkup = [
      ...collections.map((collection) => renderPredictiveSearchLink(collection, 'Collection')),
      ...pages.map((page) => renderPredictiveSearchLink(page, 'Page')),
      ...articles.map((article) => renderPredictiveSearchLink(article, 'Article')),
    ].filter(Boolean).join('');
    let viewAllUrl = '';

    try {
      const url = new URL(searchUrl || `${getLocaleRoot()}search`, window.location.origin);
      url.searchParams.set('q', String(query || '').trim());
      url.searchParams.set('type', 'product');
      viewAllUrl = url.toString();
    } catch (error) {
      viewAllUrl = `${searchUrl || `${getLocaleRoot()}search`}?q=${encodeURIComponent(String(query || '').trim())}`;
    }

    if (!productMarkup && !linkMarkup) {
      return `
        <div class="ks-header__predictive-empty">
          <p class="ks-header__predictive-copy">No results found for "${escapeHtml(query)}".</p>
          <a class="button button--outline button--small" href="${escapeHtml(viewAllUrl)}">View full search</a>
        </div>
      `;
    }

    return `
      ${productMarkup ? `
        <div class="ks-header__predictive-group ks-header__predictive-group--products">
          <p class="ks-header__predictive-title">Products</p>
          <div class="ks-header__predictive-products">${productMarkup}</div>
        </div>
      ` : ''}
      ${linkMarkup ? `
        <div class="ks-header__predictive-group ks-header__predictive-group--links">
          <p class="ks-header__predictive-title">More results</p>
          <div class="ks-header__predictive-links">${linkMarkup}</div>
        </div>
      ` : ''}
      <a class="button button--primary button--small ks-header__predictive-view-all" href="${escapeHtml(viewAllUrl)}">View all results</a>
    `;
  }

  async function fetchDocumentMarkup(url, { force = false } = {}) {
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.hash = '';
    const cacheKey = requestUrl.toString();

    if (!force && documentMarkupCache.has(cacheKey)) {
      return documentMarkupCache.get(cacheKey);
    }

    const requestPromise = (async () => {
      const response = await fetch(requestUrl.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load product details.');
      }

      return response.text();
    })();

    documentMarkupCache.set(cacheKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      documentMarkupCache.delete(cacheKey);
      throw error;
    }
  }

  async function fetchSectionMarkup(url, sectionId, { force = false } = {}) {
    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', sectionId);
    const cacheKey = requestUrl.toString();

    if (!force && sectionMarkupCache.has(cacheKey)) {
      return sectionMarkupCache.get(cacheKey);
    }

    const requestPromise = (async () => {
      const response = await fetch(requestUrl.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error('Unable to load product details.');
      }

      return response.text();
    })();

    sectionMarkupCache.set(cacheKey, requestPromise);

    try {
      return await requestPromise;
    } catch (error) {
      sectionMarkupCache.delete(cacheKey);
      throw error;
    }
  }

  function getDialogAnimationDuration(dialog) {
    if (!dialog || reducedMotionMedia.matches) return 0;
    if (dialog.hasAttribute('data-native-cart-root')) return 320;

    return Math.max(0, Number(dialog.dataset.dialogDuration || 220));
  }

  function isMobileHeaderDrawer(dialog) {
    return Boolean(
      dialog?.matches?.('[data-header-drawer]')
      && window.matchMedia('(max-width: 989px)').matches
    );
  }

  function setMobileHeaderDrawerState(dialog, isOpen) {
    if (!dialog?.matches?.('[data-header-drawer]')) return;

    const sectionId = String(dialog.dataset.headerDrawerFor || '').trim();
    const header = sectionId ? document.getElementById(`KSHeader-${sectionId}`) : null;

    if (!header) return;

    if (isOpen) {
      const drawerTop = Math.max(0, Math.round(header.getBoundingClientRect().bottom));

      dialog.style.setProperty('--ks-mobile-drawer-top', `${drawerTop}px`);
      header.dataset.mobileDrawerOpen = 'true';
      header.dataset.menuOpen = 'true';
      header.classList.remove('is-scrolled-down');
    } else {
      delete header.dataset.mobileDrawerOpen;
      dialog.style.removeProperty('--ks-mobile-drawer-top');

      if (!header.querySelector('[data-header-nav-item][data-nav-open="true"]')) {
        delete header.dataset.menuOpen;
      }
    }

    collect(header, `[aria-controls="${dialog.id}"][data-menu-open]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      trigger.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    });
  }

  function openDialog(dialog, { trigger = null } = {}) {
    if (!dialog || typeof dialog.showModal !== 'function') return;

    if (dialog.hasAttribute('data-native-cart-root')) {
      if (dialog.open && dialog.dataset.dialogClosing !== 'true') return;
      window.clearTimeout(dialog.__closeTimer);
      delete dialog.dataset.dialogClosing;
      if (trigger) dialog.__returnFocusEl = trigger;
      // Focus the stationary shell first: showModal's automatic focus must not
      // scroll a translated child into view before its entrance starts.
      dialog.setAttribute('tabindex', '-1');
      dialog.setAttribute('autofocus', '');
      dialog.dataset.nativeEntering = 'true';
      dialog.dataset.dialogState = 'open';
      if (!dialog.open) dialog.showModal();
      dialog.focus({ preventScroll: true });
      const panel = dialog.querySelector('[data-dialog-panel]');
      const motion = !reducedMotionMedia.matches && panel?.animate([
        { transform: 'translate3d(100%,0,0)' },
        { transform: 'translate3d(0,0,0)' },
      ], { duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)' });
      dialog.__nativeEntrance = (motion ? motion.finished.catch(() => {}) : Promise.resolve()).then(() => {
        delete dialog.dataset.nativeEntering;
        if (dialog.open && dialog.dataset.dialogClosing !== 'true') {
          dialog.querySelector('[data-dialog-initial-focus]')?.focus({ preventScroll: true });
        }
      });
      dialog.__nativeMotion = motion;
      return;
    }

    window.clearTimeout(dialog.__closeTimer);
    delete dialog.dataset.dialogClosing;

    if (trigger) {
      dialog.__returnFocusEl = trigger;

      if (trigger.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', 'true');
      }
    }

    if (!dialog.open) {
      if (isMobileHeaderDrawer(dialog) && typeof dialog.show === 'function') {
        dialog.show();
      } else {
        dialog.showModal();
      }
    }

    setMobileHeaderDrawerState(dialog, true);
    if (isMobileHeaderDrawer(dialog)) {
      // Warm only small menu tiles, only after menu intent; never compete with LCP.
      const connection = navigator.connection;
      if (!connection?.saveData && !/2g/.test(connection?.effectiveType || '')) {
        collect(dialog, '.ks-header__standard-mega-mobile-images img[data-deferred-image]').slice(0, 6).forEach((image) => {
          image.loading = 'eager';
          image.fetchPriority = 'low';
          hydrateDeferredImage(image);
        });
      }
    }
    dialog.dataset.dialogState = 'opening';
    if (dialog.hasAttribute('data-native-cart-root')) {
      // Establish the off-screen position before starting the transition.
      dialog.querySelector('[data-dialog-panel]')?.getBoundingClientRect();
    }

    window.requestAnimationFrame(() => {
      dialog.dataset.dialogState = 'open';
      syncDrawerStacks(dialog);
      const initialFocus = dialog.querySelector('[data-dialog-initial-focus]')
        || dialog.querySelector(
          'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href]'
        );

      if (initialFocus && typeof initialFocus.focus === 'function') {
        initialFocus.focus({ preventScroll: true });
      } else if (typeof dialog.focus === 'function') {
        if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');
        dialog.focus({ preventScroll: true });
      }
      collect(dialog, '[data-cart-drawer-scroll]').forEach((container) => {
        container.__updateCartDrawerScrollbar?.();
      });
    });
  }

  function closeDialog(dialog) {
    if (!dialog || typeof dialog.close !== 'function' || !dialog.open) return;
    if (dialog.dataset.dialogClosing === 'true') return;

    const duration = getDialogAnimationDuration(dialog);

    dialog.dataset.dialogClosing = 'true';
    if (dialog.__nativeMotion?.playState === 'running') dialog.__nativeMotion.finish();
    delete dialog.dataset.nativeEntering;
    dialog.dataset.dialogState = 'closing';
    setMobileHeaderDrawerState(dialog, false);

    const finalize = () => {
      if (dialog.open) {
        const preservePosition = dialog.hasAttribute('data-search-dropdown');
        const x = window.scrollX;
        const y = window.scrollY;
        dialog.close();
        if (preservePosition) window.scrollTo({ left: x, top: y, behavior: 'instant' });
      }
    };

    if (duration === 0) {
      finalize();
      return;
    }

    dialog.__closeTimer = window.setTimeout(finalize, duration);
  }

  async function replaceProductSection(currentSection, targetUrl, {
    announceLabel = 'Product',
    force = false,
    moveFocus = true,
  } = {}) {
    const sectionId = String(currentSection?.dataset?.productSectionId || '').trim();
    const currentMain = currentSection?.closest?.('#MainContent') || document.getElementById('MainContent');

    if (!currentSection || !currentMain || !sectionId || !targetUrl) {
      throw new Error('Unable to load product details.');
    }

    currentMain.dataset.loading = 'true';
    currentMain.setAttribute('aria-busy', 'true');

    const markup = await fetchDocumentMarkup(targetUrl, { force });
    const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    const nextMain = parsedDocument.getElementById('MainContent');
    const replacement = nextMain?.querySelector?.('[data-product-section-id]');

    if (!nextMain || !replacement) {
      throw new Error('Unable to load product details.');
    }

    const swapMainContent = () => {
      syncManagedHead(parsedDocument);
      currentMain.replaceWith(nextMain);
      initScope(nextMain);
      refreshOkendoWidgets(nextMain);
    };
    const canUseViewTransition = (
      typeof document.startViewTransition === 'function'
      && !reducedMotionMedia.matches
    );

    if (canUseViewTransition) {
      currentMain.style.viewTransitionName = 'ks-product-main';
      nextMain.style.viewTransitionName = 'ks-product-main';

      try {
        const transition = document.startViewTransition(swapMainContent);

        await transition.updateCallbackDone;
        await transition.finished.catch(() => {});
      } catch (error) {
        if (currentMain.isConnected) {
          swapMainContent();
        }
      } finally {
        nextMain.style.removeProperty('view-transition-name');
      }
    } else {
      let transitionClone = null;

      if (!reducedMotionMedia.matches) {
        const currentMainRect = currentMain.getBoundingClientRect();

        transitionClone = currentMain.cloneNode(true);
        transitionClone.removeAttribute('id');
        transitionClone.querySelectorAll('[id]').forEach((node) => {
          node.removeAttribute('id');
        });
        transitionClone.setAttribute('aria-hidden', 'true');
        transitionClone.inert = true;
        transitionClone.classList.add('ks-product-main-transition-clone');
        transitionClone.style.setProperty('--ks-product-transition-top', `${currentMainRect.top}px`);
        transitionClone.style.setProperty('--ks-product-transition-left', `${currentMainRect.left}px`);
        transitionClone.style.setProperty('--ks-product-transition-width', `${currentMainRect.width}px`);
        transitionClone.style.setProperty('--ks-product-transition-height', `${currentMainRect.height}px`);
        document.body.appendChild(transitionClone);
      }

      swapMainContent();

      if (transitionClone && typeof transitionClone.animate === 'function') {
        const oldAnimation = transitionClone.animate(
          [{ opacity: 1 }, { opacity: 0 }],
          { duration: 260, easing: 'ease-out', fill: 'forwards' }
        );
        const newAnimation = nextMain.animate(
          [{ opacity: 0 }, { opacity: 1 }],
          { duration: 300, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'both' }
        );

        await Promise.allSettled([oldAnimation.finished, newAnimation.finished]);
        transitionClone.remove();
      } else if (transitionClone) {
        transitionClone.remove();
        nextMain.classList.add('ks-product-main--ajax-enter');
      } else if (!reducedMotionMedia.matches) {
        nextMain.classList.add('ks-product-main--ajax-enter');
      }
    }

    nextMain.removeAttribute('aria-busy');
    delete nextMain.dataset.loading;

    if (moveFocus) {
      const focusTarget = nextMain.querySelector('[data-product-page-title]') || replacement;

      if (focusTarget && typeof focusTarget.focus === 'function') {
        focusTarget.focus({ preventScroll: true });
      }
    }

    announce(`${replacement.dataset.productTitle || announceLabel} loaded.`);

    return replacement;
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Unable to load cart.');
    }

    return response.json();
  }

  function updateCartCount(count) {
    collect(document, '[data-cart-count]').forEach((node) => {
      const normalizedCount = Number(count || 0);

      node.textContent = normalizedCount > 99 ? '99+' : normalizedCount;
      node.hidden = normalizedCount === 0;
    });
  }

  function isUpcartAvailable() {
    if (body.dataset.nativeCart === 'true') return false;
    return typeof window.upcartOpenCart === 'function';
  }

  function openUpcartCart() {
    if (!isUpcartAvailable()) return false;

    const nativeDrawer = document.querySelector('[data-cart-drawer-root][open]');
    if (nativeDrawer) closeDialog(nativeDrawer);

    window.upcartOpenCart();
    return true;
  }

  function subscribeToUpcartCart() {
    if (document.documentElement.dataset.upcartCartSubscribed === 'true') return true;
    if (typeof window.upcartSubscribeCartLoaded !== 'function') return false;

    window.upcartSubscribeCartLoaded((event) => {
      const items = event?.cart?.items;
      if (!Array.isArray(items)) return;

      const itemCount = items.reduce((total, item) => (
        total + Number(item?.quantity || 0)
      ), 0);

      updateCartCount(itemCount);
    });

    document.documentElement.dataset.upcartCartSubscribed = 'true';
    fetchCart().then((cart) => updateCartCount(cart.item_count)).catch(() => {});
    return true;
  }

  function initUpcartBridge(scope = document) {
    if (body.dataset.nativeCart === 'true') {
      collect(scope, '[data-upcart-trigger]').forEach((trigger) => {
        if (trigger.dataset.nativeCartTriggerBound) return;
        trigger.dataset.nativeCartTriggerBound = 'true';
        trigger.addEventListener('click', (event) => {
          const drawer = document.querySelector('[data-native-cart-root]');
          if (!drawer) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          openDialog(drawer, { trigger });
          if (!nativeCartBusy) refreshHeaderCartState().then(() => {
            const activeDrawer = document.querySelector('[data-native-cart-root][open]');
            if (activeDrawer && !activeDrawer.contains(document.activeElement)) {
              activeDrawer.querySelector('[data-dialog-initial-focus]')?.focus({ preventScroll: true });
            }
          }).catch(() => announce('Unable to refresh cart. Please try again.'));
        }, true);
      });
      return;
    }
    collect(scope, '[data-upcart-trigger]').forEach((trigger) => {
      if (trigger.dataset.upcartBridgeInitialized === 'true') return;
      trigger.dataset.upcartBridgeInitialized = 'true';

      trigger.addEventListener('click', (event) => {
        if (!isUpcartAvailable()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        openUpcartCart();
      }, true);
    });

    if (subscribeToUpcartCart()) return;
    if (document.documentElement.dataset.upcartBridgePolling === 'true') return;

    document.documentElement.dataset.upcartBridgePolling = 'true';
    let attempts = 0;

    const retrySubscription = () => {
      attempts += 1;

      if (subscribeToUpcartCart() || attempts >= 100) {
        delete document.documentElement.dataset.upcartBridgePolling;
        return;
      }

      window.setTimeout(retrySubscription, 200);
    };

    window.setTimeout(retrySubscription, 0);
  }

  async function refreshHeaderCartState({ openDrawer = false } = {}) {
    const header = document.querySelector('[data-header-section-id]');
    const currentDrawer = document.querySelector('[data-cart-drawer-root]');

    if (!header) return;

    const markup = await fetchSectionMarkup(
      window.location.href,
      header.dataset.headerSectionId,
      { force: true }
    );
    const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    const nextDrawer = parsedDocument.querySelector('[data-cart-drawer-root]');

    if (currentDrawer?.__nativeEntrance) await currentDrawer.__nativeEntrance;

    if (currentDrawer && nextDrawer) {
      const currentPanel = currentDrawer.querySelector('.nc-panel');
      const nextPanel = nextDrawer.querySelector('.nc-panel');
      if (currentPanel && nextPanel) {
        // Keep the animated shell mounted while refreshed cart content arrives.
        currentPanel.innerHTML = nextPanel.innerHTML;
      } else {
        currentDrawer.innerHTML = nextDrawer.innerHTML;
      }
      delete currentDrawer.dataset.cartLoading;
      initScope(currentDrawer);
    }

    if (openDrawer && !openUpcartCart() && currentDrawer && !currentDrawer.open) {
      openDialog(currentDrawer);
    }
  }

  function openCartDrawerLoading(sourceForm = null) {
    if (isUpcartAvailable()) return null;

    const drawer = document.querySelector('[data-cart-drawer-root]');

    if (!drawer) return null;

    // Native cart opens once the successful add has supplied its real content;
    // do not slide in an empty loading panel and replace it mid-entrance.
    if (drawer.hasAttribute('data-native-cart-root')) return drawer;

    const productRoot = sourceForm?.closest('[data-product-section-id], [data-product-card]');
    const productTitle = productRoot?.querySelector(
      '[data-product-page-title], .product-card__title'
    )?.textContent?.trim();
    const productImage = productRoot?.querySelector(
      '.ks-product-media__item.is-active img, .product-card__image'
    );
    const loadingTitle = drawer.querySelector('[data-cart-loading-title]');
    const loadingMedia = drawer.querySelector('[data-cart-loading-media]');

    if (loadingTitle) {
      loadingTitle.textContent = productTitle || '';
    }

    if (loadingMedia) {
      const imageUrl = productImage?.currentSrc || productImage?.src || '';
      loadingMedia.style.backgroundImage = imageUrl ? `url("${imageUrl}")` : '';
    }

    drawer.dataset.cartLoading = 'true';
    openDialog(drawer);
    return drawer;
  }

  function initDialogs(scope = document) {
    collect(scope, '[data-dialog-trigger], [data-menu-open], [data-filter-open]').forEach((trigger) => {
      if (trigger.dataset.dialogInitialized === 'true') return;
      trigger.dataset.dialogInitialized = 'true';

      trigger.addEventListener('click', (event) => {
        const dialogId = trigger.getAttribute('data-dialog-trigger') || trigger.getAttribute('aria-controls');
        const dialog = dialogId ? document.getElementById(dialogId) : null;

        if (!dialog || typeof dialog.showModal !== 'function') return;

        event.preventDefault();

        if (dialog.open && isMobileHeaderDrawer(dialog)) {
          closeDialog(dialog);
          return;
        }

        openDialog(dialog, { trigger });
      });
    });

    collect(scope, '[data-dialog-close], [data-menu-close], [data-filter-close]').forEach((button) => {
      if (button.dataset.dialogInitialized === 'true') return;
      button.dataset.dialogInitialized = 'true';

      button.addEventListener('click', () => {
        const dialog = button.closest('dialog');
        if (dialog) closeDialog(dialog);
      });
    });

    collect(scope, 'dialog').forEach((dialog) => {
      if (dialog.dataset.dialogInitialized === 'true') return;
      dialog.dataset.dialogInitialized = 'true';

      dialog.addEventListener('click', (event) => {
        const panel = dialog.querySelector(
          '[data-dialog-panel], .ks-header__search-panel, .ks-header__drawer-panel, .ks-collection__drawer-panel, .ks-cart-drawer__panel'
        );
        if (panel && !panel.contains(event.target)) {
          closeDialog(dialog);
        }
      });

      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        closeDialog(dialog);
      });

      dialog.addEventListener('close', () => {
        window.clearTimeout(dialog.__closeTimer);
        delete dialog.dataset.dialogClosing;
        delete dialog.dataset.dialogState;
        setMobileHeaderDrawerState(dialog, false);

        if (dialog.id) {
          collect(document, `[aria-controls="${dialog.id}"]`).forEach((trigger) => {
            if (trigger.hasAttribute('aria-expanded')) {
              trigger.setAttribute('aria-expanded', 'false');
            }
          });
        }

        if (dialog.__returnFocusEl && dialog.__returnFocusEl.isConnected) {
          dialog.__returnFocusEl.focus({ preventScroll: true });
        }

        collect(dialog, '[data-drawer-stack]').forEach((stack) => {
          resetDrawerStack(stack);
        });

        dialog.__returnFocusEl = null;
      });
    });
  }

  function normalizeHeaderMenuHandle(value = '') {
    return value
      .trim()
      .toLowerCase()
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  }

  function initHeaderMenuButtons(scope = document) {
    const header = scope.matches?.('[data-header-section-id]')
      ? scope
      : scope.querySelector?.('[data-header-section-id]');
    const rootScope = header || document;

    collect(rootScope, '[data-header-menu-button-source]').forEach((source) => {
      const blockId = source.dataset.headerMenuButtonBlockId || '';
      const targetHandle = source.dataset.headerMenuButtonTarget || '';
      const buttonStyle = source.dataset.headerMenuButtonStyle || 'primary';
      const buttonSize = source.dataset.headerMenuButtonSize || 'small';

      if (!blockId || !targetHandle) return;

      const mountedButtons = collect(rootScope, '[data-header-menu-button-mounted]').filter(
        (mountedButton) => mountedButton.dataset.headerMenuButtonMounted === blockId
      );
      let actionButton = mountedButtons.shift() || null;
      mountedButtons.forEach((mountedButton) => mountedButton.remove());

      collect(rootScope, '.ks-header__nav-item--action-source').forEach((navItem) => {
        if (navItem.dataset.headerMenuButtonBlockId === blockId) {
          navItem.classList.remove('ks-header__nav-item--action-source');
          navItem.hidden = false;
          delete navItem.dataset.headerMenuButtonBlockId;
        }
      });

      collect(rootScope, '.ks-header__menu-link--action-source').forEach((link) => {
        if (link.dataset.headerMenuButtonBlockId === blockId) {
          link.classList.remove('ks-header__menu-link--action-source');
          link.hidden = false;
          delete link.dataset.headerMenuButtonBlockId;
        }
      });

      const matchingLinks = [];

      collect(rootScope, '[data-header-nav-handle]').forEach((navItem) => {
        if (navItem.dataset.headerNavHandle !== targetHandle) return;

        const link = navItem.querySelector(':scope > .ks-header__nav-link');
        if (!link) return;

        matchingLinks.push(link);
        navItem.classList.add('ks-header__nav-item--action-source');
        navItem.hidden = true;
        navItem.dataset.headerMenuButtonBlockId = blockId;
      });

      collect(rootScope, '.ks-header__nav--secondary a, .ks-header__drawer-link').forEach((link) => {
        if (normalizeHeaderMenuHandle(link.textContent) !== targetHandle) return;

        matchingLinks.push(link);
        link.classList.add('ks-header__menu-link--action-source');
        link.dataset.headerMenuButtonBlockId = blockId;

        if (link.closest('.ks-header__nav--secondary')) link.hidden = true;
      });

      const actions = rootScope.querySelector('.ks-header__actions');
      const linkedMenuItem = matchingLinks.find((link) => link.getAttribute('href'));
      if (!actions || !linkedMenuItem) {
        actionButton?.remove();
        return;
      }

      if (!actionButton) actionButton = document.createElement('a');
      actionButton.href = linkedMenuItem.getAttribute('href');
      actionButton.textContent = source.textContent.trim() || linkedMenuItem.textContent.trim();
      actionButton.dataset.headerMenuButtonMounted = blockId;
      actionButton.dataset.headerActionTarget = targetHandle;
      actionButton.className = `button button--${buttonStyle} button--${buttonSize} ks-header__action-button`;
      if (!actions.contains(actionButton)) actions.prepend(actionButton);
    });
  }

  function initHeaderMenus(scope = document) {
    collect(scope, '[data-header-nav-item]').forEach((item) => {
      if (item.dataset.headerNavInitialized === 'true') return;
      item.dataset.headerNavInitialized = 'true';

      const trigger = item.querySelector('.ks-header__nav-link');
      const submenu = item.querySelector('.ks-header__submenu');
      let closeTimer = 0;
      let clearActiveNestedPanel = () => {};

      if (!trigger) return;

      const clearCloseTimer = () => {
        if (!closeTimer) return;
        window.clearTimeout(closeTimer);
        closeTimer = 0;
      };

      const setOpenState = (isOpen) => {
        const isMegaMenu = item.classList.contains('ks-header__nav-item--mega');

        if (isOpen && submenu?.classList.contains('ks-header__submenu--standard-mega')) {
          alignStandardMegaSubmenu(item, submenu);
        }

        item.dataset.navOpen = isOpen ? 'true' : 'false';
        setHeaderMenuOpenState(item, isOpen);
        setMegaMenuOverlayState(item, isOpen && isMegaMenu);

        if (trigger.hasAttribute('aria-expanded')) {
          trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        }

        if (!isOpen) {
          clearActiveNestedPanel();
        }
      };

      const scheduleClose = () => {
        clearCloseTimer();
        closeTimer = window.setTimeout(() => {
          setOpenState(false);
        }, 140);
      };

      const closeSiblingMenus = () => {
        const header = item.closest('[data-header-section-id]');
        if (!header) return;

        collect(header, '[data-header-nav-item][data-nav-open="true"]').forEach((sibling) => {
          if (sibling === item) return;
          sibling.dispatchEvent(new CustomEvent('ks:header-nav-close'));
        });
      };

      item.addEventListener('ks:header-nav-close', () => {
        clearCloseTimer();
        setOpenState(false);
      });

      item.addEventListener('mouseenter', () => {
        closeSiblingMenus();
        clearCloseTimer();
        setOpenState(true);
      });

      item.addEventListener('mouseleave', () => {
        scheduleClose();
      });

      item.addEventListener('focusin', () => {
        closeSiblingMenus();
        clearCloseTimer();
        setOpenState(true);
      });

      item.addEventListener('focusout', () => {
        window.requestAnimationFrame(() => {
          if (!item.contains(document.activeElement)) {
            clearCloseTimer();
            setOpenState(false);
          }
        });
      });

      if (submenu) {
        const nestedTriggers = Array.from(submenu.querySelectorAll('[data-header-submenu-trigger]'));
        const nestedPanels = Array.from(submenu.querySelectorAll('[data-header-submenu-panel]'));
        const nestedPanelContainer = submenu.querySelector('.ks-header__submenu-panels');
        const nestedPlainLinks = Array.from(submenu.querySelectorAll('.ks-header__submenu-list > .ks-header__submenu-item > .ks-header__submenu-link:not([data-header-submenu-trigger])'));

        clearActiveNestedPanel = () => {
          nestedTriggers.forEach((nestedTrigger) => {
            nestedTrigger.setAttribute('aria-expanded', 'false');
            nestedTrigger.closest('.ks-header__submenu-item')?.classList.remove('is-active');
          });

          nestedPanels.forEach((panel) => {
            panel.classList.remove('is-active');
          });

          nestedPanelContainer?.classList.remove('is-active');
        };

        const setActiveNestedPanel = (panelId) => {
          if (!panelId) return;

          nestedTriggers.forEach((nestedTrigger) => {
            const isActive = nestedTrigger.dataset.headerSubmenuTrigger === panelId;
            nestedTrigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
            nestedTrigger.closest('.ks-header__submenu-item')?.classList.toggle('is-active', isActive);
          });

          nestedPanels.forEach((panel) => {
            panel.classList.toggle('is-active', panel.dataset.headerSubmenuPanel === panelId);
          });

          nestedPanelContainer?.classList.add('is-active');
        };

        nestedTriggers.forEach((nestedTrigger) => {
          const panelId = nestedTrigger.dataset.headerSubmenuTrigger;

          nestedTrigger.addEventListener('mouseenter', () => {
            setActiveNestedPanel(panelId);
          });

          nestedTrigger.addEventListener('focus', () => {
            setActiveNestedPanel(panelId);
          });
        });

        nestedPlainLinks.forEach((nestedPlainLink) => {
          nestedPlainLink.addEventListener('mouseenter', clearActiveNestedPanel);
          nestedPlainLink.addEventListener('focus', clearActiveNestedPanel);
        });

        submenu.addEventListener('mouseenter', () => {
          clearCloseTimer();
          setOpenState(true);
        });

        submenu.addEventListener('mouseleave', () => {
          clearActiveNestedPanel();
          scheduleClose();
        });
      }

      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        clearCloseTimer();
        setOpenState(false);
        trigger.focus({ preventScroll: true });
      });
    });
  }

  function cloneStandardMegaMenuSource(source) {
    const clone = source.cloneNode(true);

    clone.removeAttribute('data-standard-mega-source');
    delete clone.dataset.standardMegaSource;
    delete clone.dataset.standardMegaInitialized;

    return clone;
  }

  function initMobileStandardMegaMenu(menu) {
    if (!menu || menu.dataset.mobileStandardMegaInitialized === 'true') return;

    const screen = menu.closest('.ks-header__drawer-screen--standard-mega');
    const layout = menu.querySelector(':scope > .ks-header__standard-mega');

    if (!screen || !layout) return;

    menu.dataset.mobileStandardMegaInitialized = 'true';

    if (layout.classList.contains('ks-header__standard-mega--linked-image-cards')) {
      layout.classList.add('ks-header__standard-mega--mobile-linked-cards');
      return;
    }

    const columns = Array.from(layout.querySelectorAll(':scope > [data-standard-mega-column]'));
    const imageColumns = Array.from(layout.querySelectorAll(
      ':scope > [data-standard-mega-image-columns] > .ks-header__standard-mega-image-column'
    ));

    if (columns.length === 0) return;

    const heading = menu.dataset.standardMegaHeading || 'menu';
    const tabs = document.createElement('div');
    const sharedImages = document.createElement('div');
    const panelsShell = document.createElement('div');
    const panels = document.createElement('div');
    const panelsScrollbar = document.createElement('span');
    const panelsScrollbarIndicator = document.createElement('span');

    tabs.className = 'ks-header__standard-mega-mobile-tabs';
    tabs.setAttribute('role', 'tablist');
    tabs.setAttribute('aria-label', 'Menu categories');
    sharedImages.className = 'ks-header__standard-mega-mobile-images';
    panelsShell.className = 'ks-header__standard-mega-mobile-panels-shell';
    panels.className = 'ks-header__standard-mega-mobile-panels';
    panelsScrollbar.className = 'ks-header__standard-mega-mobile-panels-scrollbar';
    panelsScrollbar.setAttribute('aria-hidden', 'true');
    panelsScrollbarIndicator.className = 'ks-header__standard-mega-mobile-panels-scrollbar-indicator';
    panelsScrollbar.append(panelsScrollbarIndicator);
    panelsShell.append(panels, panelsScrollbar);

    let panelsScrollbarFrame = 0;
    const updatePanelsScrollbar = () => {
      window.cancelAnimationFrame(panelsScrollbarFrame);
      panelsScrollbarFrame = window.requestAnimationFrame(() => {
        const viewportHeight = panels.clientHeight;
        const contentHeight = panels.scrollHeight;
        const maxScroll = Math.max(0, contentHeight - viewportHeight);
        const isScrollable = viewportHeight > 0 && maxScroll > 1;

        panelsShell.classList.toggle('is-scrollable', isScrollable);

        if (!isScrollable) {
          panelsScrollbarIndicator.style.height = '';
          panelsScrollbarIndicator.style.transform = '';
          return;
        }

        const indicatorHeight = Math.max(36, viewportHeight * (viewportHeight / contentHeight));
        const availableTrack = Math.max(0, viewportHeight - indicatorHeight);
        const indicatorOffset = availableTrack * (panels.scrollTop / maxScroll);

        panelsScrollbarIndicator.style.height = `${indicatorHeight}px`;
        panelsScrollbarIndicator.style.transform = `translate3d(0, ${indicatorOffset}px, 0)`;
      });
    };

    panels.addEventListener('scroll', updatePanelsScrollbar, { passive: true });

    if (typeof ResizeObserver === 'function') {
      const panelsResizeObserver = new ResizeObserver(updatePanelsScrollbar);
      panelsResizeObserver.observe(panels);
      panelsShell.__ksStandardMegaPanelsResizeObserver = panelsResizeObserver;
    }

    if (typeof MutationObserver === 'function') {
      const panelsMutationObserver = new MutationObserver(updatePanelsScrollbar);
      panelsMutationObserver.observe(panels, { childList: true, subtree: true });
      panelsShell.__ksStandardMegaPanelsMutationObserver = panelsMutationObserver;
    }

    const activateTab = (activeIndex, focusTab = false) => {
      const tabButtons = Array.from(tabs.querySelectorAll('[role="tab"]'));
      const tabPanels = Array.from(panels.querySelectorAll('[role="tabpanel"]'));

      tabButtons.forEach((button, index) => {
        const isActive = index === activeIndex;

        button.setAttribute('aria-selected', String(isActive));
        button.tabIndex = isActive ? 0 : -1;
        button.classList.toggle('is-active', isActive);

        if (isActive && focusTab) {
          button.focus({ preventScroll: true });
          button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });

      tabPanels.forEach((panel, index) => {
        panel.hidden = index !== activeIndex;
      });

      if (
        sharedImages.childElementCount > 0
        && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        sharedImages.classList.remove('is-switching');
        void sharedImages.offsetWidth;
        sharedImages.classList.add('is-switching');
      }

      window.requestAnimationFrame(() => {
        panels.scrollTop = 0;
        updatePanelsScrollbar();
        updateDrawerStackHeight(screen.closest('[data-drawer-stack]'));
      });
    };

    columns.forEach((column, index) => {
      const columnHandle = column.dataset.columnHandle || `category-${index + 1}`;
      const columnTitle = column.dataset.columnTitle || columnHandle;
      const tabId = `KSMobileMegaTab-${heading}-${index + 1}`;
      const panelId = `KSMobileMegaPanel-${heading}-${index + 1}`;
      const tab = document.createElement('button');
      const panel = document.createElement('section');

      tab.type = 'button';
      tab.id = tabId;
      tab.className = 'ks-header__standard-mega-mobile-tab';
      tab.textContent = columnTitle;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.addEventListener('click', () => activateTab(index));
      tab.addEventListener('keydown', (event) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

        event.preventDefault();

        let nextIndex = index;
        if (event.key === 'ArrowLeft') nextIndex = (index - 1 + columns.length) % columns.length;
        if (event.key === 'ArrowRight') nextIndex = (index + 1) % columns.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = columns.length - 1;

        activateTab(nextIndex, true);
      });

      panel.id = panelId;
      panel.className = 'ks-header__standard-mega-mobile-panel';
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.append(column);

      tabs.append(tab);
      panels.append(panel);
    });

    imageColumns.forEach((imageColumn) => sharedImages.append(imageColumn));

    // Hidden menu imagery stays deferred until the drawer is opened.

    sharedImages.addEventListener('animationend', (event) => {
      if (event.animationName === 'ks-standard-mega-mobile-images-in') {
        sharedImages.classList.remove('is-switching');
      }
    });

    if (sharedImages.childElementCount > 0) {
      layout.replaceChildren(tabs, panelsShell, sharedImages);
    } else {
      layout.replaceChildren(tabs, panelsShell);
    }

    activateTab(0);
    updatePanelsScrollbar();
  }

  function setMegaMenuOverlayState(navItem, isOpen) {
    const header = navItem?.closest?.('[data-header-section-id]');

    if (!header || header.dataset.megaMenuOverlay !== 'true') return;

    if (isOpen) {
      header.dataset.megaOverlayOpen = 'true';
      return;
    }

    const hasOpenMegaMenu = collect(header, '.ks-header__nav-item--mega[data-nav-open="true"]').length > 0;

    if (hasOpenMegaMenu) return;

    delete header.dataset.megaOverlayOpen;
  }

  function setHeaderMenuOpenState(navItem, isOpen) {
    const header = navItem?.closest?.('[data-header-section-id]');

    if (!header) return;

    if (isOpen) {
      header.dataset.menuOpen = 'true';
      return;
    }

    const hasOpenMenu = collect(header, '[data-header-nav-item][data-nav-open="true"]').length > 0;

    if (hasOpenMenu) return;

    delete header.dataset.menuOpen;
  }

  function alignStandardMegaSubmenu(navItem, submenu = navItem?.querySelector?.(':scope > .ks-header__submenu--standard-mega')) {
    if (!navItem || !submenu || !submenu.classList.contains('ks-header__submenu--standard-mega')) return;

    const navItemRect = navItem.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;

    submenu.style.setProperty('--ks-standard-mega-panel-left', `${navItemRect.left * -1}px`);
    submenu.style.setProperty('--ks-standard-mega-panel-width', `${Math.round(viewportWidth)}px`);
  }

  function alignStandardMegaSubmenus(scope = document) {
    collect(scope, '.ks-header__nav-item--mega').forEach((navItem) => {
      alignStandardMegaSubmenu(navItem);
    });
  }

  function upgradeStandardMegaNavItems(scope = document) {
    const header = scope.matches?.('[data-header-section-id]')
      ? scope
      : scope.querySelector?.('[data-header-section-id]');
    const rootScope = header || document;

    collect(rootScope, '[data-standard-mega-source]').forEach((source) => {
      const heading = source.dataset.standardMegaHeading;
      const sourceColorScheme = source.dataset.standardMegaColorScheme || '';
      if (!heading) return;

      collect(rootScope, '[data-header-nav-handle]').forEach((navItem) => {
        if (navItem.dataset.headerNavHandle !== heading) return;
        if (navItem.dataset.standardMegaNavUpgraded === 'true') return;

        let submenu = navItem.querySelector(':scope > .ks-header__submenu');
        const trigger = navItem.querySelector(':scope > .ks-header__nav-link');

        if (!submenu) {
          submenu = document.createElement('div');
          submenu.className = 'ks-header__submenu color-scheme-default';
          navItem.append(submenu);
        }

        navItem.classList.add('ks-header__nav-item--has-children', 'ks-header__nav-item--mega');
        navItem.dataset.headerNavItem = '';
        navItem.dataset.standardMegaNavUpgraded = 'true';
        delete navItem.dataset.headerNavInitialized;

        if (trigger) {
          trigger.setAttribute('aria-haspopup', 'true');
          trigger.setAttribute('aria-expanded', 'false');
        }

        submenu.classList.remove('ks-header__submenu--with-content', 'ks-header__submenu--nested');
        submenu.classList.add('ks-header__submenu--content-only', 'ks-header__submenu--standard-mega');
        if (sourceColorScheme) {
          Array.from(submenu.classList)
            .filter((className) => className.startsWith('color-'))
            .forEach((className) => submenu.classList.remove(className));
          submenu.classList.add(`color-${sourceColorScheme}`);
        }
        submenu.replaceChildren();

        const headerContainer = navItem.closest('[data-header-section-id]')?.querySelector('.ks-header__main > .theme-container');
        const wrapper = document.createElement('div');
        wrapper.className = headerContainer
          ? `ks-header__submenu-content ${headerContainer.className}`
          : 'ks-header__submenu-content theme-container theme-container--default';
        wrapper.append(cloneStandardMegaMenuSource(source));
        submenu.append(wrapper);

        alignStandardMegaSubmenu(navItem, submenu);
      });
    });
  }

  function initStandardMegaMenus(scope = document) {
    upgradeStandardMegaNavItems(scope);

    collect(scope, '[data-standard-mega-mount]').forEach((mount) => {
      if (mount.dataset.standardMegaMounted === 'true') return;

      const heading = mount.dataset.standardMegaHeading;
      const header = mount.closest('[data-header-section-id]') || document;
      const source = collect(header, '[data-standard-mega-source]').find((candidate) => (
        candidate.dataset.standardMegaHeading === heading
      ));

      if (!heading || !source) return;

      mount.replaceChildren(cloneStandardMegaMenuSource(source));
      mount.dataset.standardMegaMounted = 'true';
    });

    collect(scope, '[data-mobile-editorial-mega-mount]').forEach((mount) => {
      if (mount.dataset.mobileMegaMounted === 'true') return;

      const heading = mount.dataset.standardMegaHeading;
      const header = mount.closest('[data-header-section-id]') || document;
      const source = collect(header, '[data-standard-mega-source]').find((candidate) => (
        candidate.dataset.standardMegaHeading === heading
      ));

      if (!heading || !source) return;

      const screen = mount.closest('[data-drawer-screen]');
      const fallbackNavigation = screen?.querySelector(':scope > .ks-header__drawer-nav');
      const isEditorialMenu = source.classList.contains('ks-header__mega-block--editorial');
      const isStandardMenu = source.classList.contains('ks-header__mega-block--standard');
      const mobileMenu = cloneStandardMegaMenuSource(source);

      fallbackNavigation?.remove();
      screen?.classList.toggle('ks-header__drawer-screen--editorial', isEditorialMenu);
      screen?.classList.toggle('ks-header__drawer-screen--standard-mega', isStandardMenu);

      if (screen) {
        Array.from(screen.classList)
          .filter((className) => className.startsWith('color-'))
          .forEach((className) => screen.classList.remove(className));
        screen.classList.add('color-scheme-1');
      }

      if (isStandardMenu) {
        Array.from(mobileMenu.classList)
          .filter((className) => className.startsWith('color-'))
          .forEach((className) => mobileMenu.classList.remove(className));
        mobileMenu.classList.add('color-scheme-1');
      }

      mount.replaceChildren(mobileMenu);
      mount.hidden = false;
      mount.dataset.mobileMegaMounted = 'true';
    });

    collect(scope, '[data-standard-mega-menu]').forEach((menu) => {
      if (menu.dataset.standardMegaInitialized === 'true') return;
      menu.dataset.standardMegaInitialized = 'true';

      const imageColumns = menu.querySelector('[data-standard-mega-image-columns]');
      const itemsByHandle = new Map();
      const linkedCardsByHandle = new Map();

      collect(menu, '[data-standard-mega-linked-handle]').forEach((card) => {
        const handle = card.dataset.standardMegaLinkedHandle || '';
        if (handle) linkedCardsByHandle.set(handle, card);
      });

      collect(menu, '[data-standard-mega-item]').forEach((item) => {
        const itemHandle = item.dataset.itemHandle || '';

        if (!itemHandle) return;

        if (!itemsByHandle.has(itemHandle)) {
          itemsByHandle.set(itemHandle, []);
        }
        itemsByHandle.get(itemHandle).push(item);
      });

      collect(menu, 'template[data-standard-mega-override]').forEach((template) => {
        const targetHandle = template.dataset.targetHandle || '';
        const linkedCard = linkedCardsByHandle.get(targetHandle);

        if (linkedCard && template.content) {
          const linkedFragment = template.content.cloneNode(true);
          const customImage = linkedFragment.querySelector('.ks-header__standard-mega-image');
          const linkedMedia = linkedCard.querySelector('.ks-header__standard-mega-linked-media');
          const customMedia = linkedFragment.querySelector('.ks-header__standard-mega-media');
          const customDescription = linkedFragment.querySelector('[data-standard-mega-description]');
          const linkedDescription = linkedCard.querySelector('[data-standard-mega-description]');

          if (customImage && linkedMedia) {
            customImage.className = 'ks-header__standard-mega-linked-image';
            linkedMedia.replaceChildren(customImage);
          }

          const customRatioClass = Array.from(customMedia?.classList || [])
            .find((className) => (
              className.startsWith('ks-header__standard-mega-media--')
            ));

          if (customRatioClass && linkedMedia) {
            Array.from(linkedMedia.classList)
              .filter((className) => className.startsWith('ks-header__standard-mega-linked-media--'))
              .forEach((className) => linkedMedia.classList.remove(className));
            linkedMedia.classList.add(customRatioClass.replace(
              'ks-header__standard-mega-media--',
              'ks-header__standard-mega-linked-media--'
            ));
          }

          if (customDescription && linkedDescription) {
            linkedDescription.innerHTML = customDescription.innerHTML;
            linkedDescription.hidden = false;
          }
          return;
        }

        const fallbackItem = itemsByHandle.get(targetHandle)?.[0];

        if (!targetHandle || !fallbackItem || !template.content || !imageColumns) return;

        const fragment = template.content.cloneNode(true);
        const imageColumn = fragment.querySelector('[data-standard-mega-override-column]');

        if (!imageColumn) return;

        const fallbackUrl = fallbackItem.dataset.itemUrl || '#';
        const fallbackTitle = fallbackItem.dataset.itemTitle || '';

        collect(imageColumn, '[data-standard-mega-default-link]').forEach((link) => {
          const href = link.getAttribute('href');
          if (!href || href === '#') {
            link.setAttribute('href', fallbackUrl);
          }
        });

        const title = imageColumn.querySelector('[data-standard-mega-title]');
        if (title && !title.textContent.trim()) {
          title.textContent = fallbackTitle;
        }

        const resolvedParentHandle = fallbackItem.dataset.parentHandle || '';
        if (resolvedParentHandle) {
          imageColumn.dataset.standardMegaParentHandle = resolvedParentHandle;
        }

        fallbackItem.dataset.standardMegaHasImage = 'true';
        imageColumn.hidden = false;
        imageColumns.append(imageColumn);
      });

      collect(menu, 'template[data-editorial-mega-override]').forEach((template) => {
        const targetHandle = template.dataset.targetHandle || '';
        const item = targetHandle
          ? menu.querySelector(`[data-editorial-mega-item="${CSS.escape(targetHandle)}"]`)
          : null;

        if (!item || !template.content) return;

        const fragment = template.content.cloneNode(true);
        const customImage = fragment.querySelector('.ks-header__editorial-mega-item-image');
        const customDescription = fragment.querySelector('[data-editorial-mega-description]');
        const itemMedia = item.querySelector('.ks-header__editorial-mega-item-media');
        const itemDescription = item.querySelector('[data-editorial-mega-item-description]');

        if (customImage && itemMedia) {
          itemMedia.replaceChildren(customImage);
        }

        if (customDescription && itemDescription) {
          itemDescription.innerHTML = customDescription.innerHTML;
          itemDescription.hidden = false;
        }
      });

      initMobileStandardMegaMenu(menu);
    });

    alignStandardMegaSubmenus(scope);

    if (window.__ksStandardMegaResizeInitialized !== true) {
      window.__ksStandardMegaResizeInitialized = true;
      window.addEventListener('resize', () => {
        window.requestAnimationFrame(() => {
          alignStandardMegaSubmenus(document);
        });
      }, { passive: true });
    }
  }

  function updateDrawerStackHeight(stack) {
    if (!stack) return;

    const activeScreen = Array.from(stack.querySelectorAll('[data-drawer-screen]')).find((screen) => (
      screen.dataset.drawerState === 'active'
    ));

    if (!activeScreen) return;

    const panel = stack.closest('.ks-header__drawer-panel');
    const panelRect = panel?.getBoundingClientRect();
    const stackRect = stack.getBoundingClientRect();
    const availableHeight = panelRect
      ? Math.max(0, panelRect.bottom - stackRect.top)
      : window.innerHeight;

    const nextHeight = Math.ceil(availableHeight);

    stack.style.height = `${nextHeight}px`;
    activeScreen.style.height = `${nextHeight}px`;
  }

  function syncDrawerStacks(scope = document) {
    collect(scope, '[data-drawer-stack]').forEach((stack) => {
      updateDrawerStackHeight(stack);
    });
  }

  function setActiveDrawerScreen(stack, targetId = 'root') {
    if (!stack) return;

    const screens = Array.from(stack.querySelectorAll('[data-drawer-screen]'));
    const target = screens.find((screen) => screen.dataset.drawerScreen === targetId) || screens[0];

    if (!target) return;

    const targetPath = String(target.dataset.drawerPath || 'root');

    collect(target, 'img[data-deferred-image]').forEach((image) => {
      image.loading = 'eager';
      hydrateDeferredImage(image);
    });

    screens.forEach((screen) => {
      const screenPath = String(screen.dataset.drawerPath || 'root');
      let state = 'future';

      if (screen === target) {
        state = 'active';
      } else if (screenPath === 'root' && targetPath !== 'root') {
        state = 'history';
      } else if (screenPath !== 'root' && targetPath.startsWith(`${screenPath}.`)) {
        state = 'history';
      }

      screen.dataset.drawerState = state;
      screen.setAttribute('aria-hidden', state === 'active' ? 'false' : 'true');
    });

    window.requestAnimationFrame(() => {
      updateDrawerStackHeight(stack);
    });
  }

  function resetDrawerStack(stack) {
    setActiveDrawerScreen(stack, 'root');
  }

  function initDrawerMenus(scope = document) {
    collect(scope, '[data-drawer-stack]').forEach((stack) => {
      if (stack.dataset.drawerInitialized === 'true') {
        updateDrawerStackHeight(stack);
        return;
      }

      stack.dataset.drawerInitialized = 'true';

      collect(stack, '[data-drawer-target]').forEach((button) => {
        button.addEventListener('click', () => {
          const targetId = String(button.dataset.drawerTarget || '').trim();

          if (!targetId) return;

          setActiveDrawerScreen(stack, targetId);
        });
      });

      window.addEventListener('resize', () => {
        updateDrawerStackHeight(stack);
      });

      setActiveDrawerScreen(stack, 'root');
    });
  }

  function initPredictiveSearch(scope = document) {
    collect(scope, '[data-predictive-search]').forEach((dialog) => {
      if (dialog.dataset.predictiveSearchInitialized === 'true') return;
      dialog.dataset.predictiveSearchInitialized = 'true';

      const dialogId = dialog.id;
      const header = dialog.closest('.ks-header') || document;
      const triggers = collect(header, `[data-predictive-search-open][aria-controls="${dialogId}"]`);
      const closeButtons = collect(dialog, '[data-predictive-search-close]');
      const form = dialog.querySelector('[data-predictive-search-form]');
      const input = dialog.querySelector('[data-predictive-search-input]');
      const results = dialog.querySelector('[data-predictive-search-results]');
      const status = dialog.querySelector('[data-predictive-search-status]');
      const searchUrl = String(dialog.dataset.searchUrl || `${getLocaleRoot()}search`).trim();
      const searchFormTypes = 'product';
      const searchTypes = 'product';
      const searchLimit = Number(dialog.dataset.predictiveSearchLimit || 6);
      const defaultMarkup = String(results?.innerHTML || '');
      let searchTimer = null;
      let searchController = null;
      let searchRequestId = 0;

      function resetSearchUi() {
        if (results) {
          results.innerHTML = defaultMarkup;
          initScope(results);
        }

        if (status) {
          status.textContent = '';
        }
      }

      function closeSearch() {
        window.clearTimeout(searchTimer);
        searchRequestId += 1;

        if (searchController) {
          searchController.abort();
          searchController = null;
        }

        closeDialog(dialog);
      }

      async function runSearch() {
        const query = String(input?.value || '').trim();

        if (!results) return;

        if (searchController) {
          searchController.abort();
          searchController = null;
        }

        if (query.length < 2) {
          resetSearchUi();
          return;
        }

        const requestId = searchRequestId + 1;
        searchRequestId = requestId;
        searchController = new AbortController();

        if (status) {
          status.textContent = 'Searching...';
        }

        try {
          const data = await loadPredictiveSearchResults({
            query,
            limit: searchLimit,
            signal: searchController.signal,
            types: searchTypes,
          });

          if (requestId !== searchRequestId) return;

          results.innerHTML = renderPredictiveSearchResults(data, {
            query,
            searchFormTypes,
            searchUrl,
          });
          initScope(results);

          if (status) {
            status.textContent = '';
          }
        } catch (error) {
          if (error?.name === 'AbortError' || requestId !== searchRequestId) {
            return;
          }

          results.innerHTML = `<div class="ks-header__predictive-empty"><p class="ks-header__predictive-copy">${escapeHtml(error?.error || 'Unable to search right now.')}</p></div>`;

          if (status) {
            status.textContent = '';
          }
        } finally {
          if (requestId === searchRequestId) {
            searchController = null;
          }
        }
      }

      function scheduleSearch(delay = 160) {
        window.clearTimeout(searchTimer);
        searchRequestId += 1;
        searchController?.abort();
        searchController = null;
        searchTimer = window.setTimeout(() => {
          runSearch();
        }, delay);
      }

      function syncSearchViewport() {
        if (!dialog.open) return;
        header.classList.remove('is-scrolled-down');
        if (header.classList.contains('ks-header--transparent')) header.classList.add('is-scrolled');
        const viewport = window.visualViewport;
        const bottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
        const top = header.getBoundingClientRect?.().bottom || 0;
        dialog.style.setProperty('--search-viewport-height', `${Math.max(0, bottom - top)}px`);
      }

      function openSearch(trigger) {
        const mobile = window.matchMedia('(max-width: 989px)').matches;
        dialog.toggleAttribute('data-search-dropdown', mobile);
        dialog.toggleAttribute('scroll-lock', !mobile);
        if (mobile) {
          const x = window.scrollX;
          const y = window.scrollY;
          window.clearTimeout(dialog.__closeTimer);
          delete dialog.dataset.dialogClosing;
          dialog.__returnFocusEl = trigger;
          trigger.setAttribute('aria-expanded', 'true');
          // Focus the close control initially so Safari does not open the keyboard.
          closeButtons[0]?.setAttribute('autofocus', '');
          if (!dialog.open) dialog.show();
          closeButtons[0]?.removeAttribute('autofocus');
          dialog.dataset.dialogState = 'open';
          window.scrollTo({ left: x, top: y, behavior: 'instant' });
        } else {
          openDialog(dialog, { trigger });
        }
        syncSearchViewport();

        window.requestAnimationFrame(() => {
          if (!input) return;

          if (!mobile || document.documentElement.dataset.inputModality === 'keyboard') {
            input.focus({ preventScroll: true });
          }

          if (String(input.value || '').trim().length >= 2) {
            scheduleSearch(0);
          } else {
            resetSearchUi();
          }
        });
      }

      triggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          if (dialog.open) closeSearch();
          else openSearch(trigger);
        });
      });

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          closeSearch();
        });
      });

      dialog.addEventListener('close', () => {
        dialog.style.removeProperty('--search-viewport-height');
        window.dispatchEvent(new Event('scroll'));
        resetSearchUi();
      });

      const dismissOutside = (event) => {
        if (!dialog.open || !dialog.hasAttribute('data-search-dropdown')) return;
        if (!dialog.contains(event.target) && !triggers.some((trigger) => trigger.contains(event.target))) closeSearch();
      };
      const dismissEscape = (event) => {
        if (event.key === 'Escape' && dialog.open && dialog.hasAttribute('data-search-dropdown')) {
          event.preventDefault();
          closeSearch();
        }
      };
      document.addEventListener('pointerdown', dismissOutside);
      document.addEventListener('keydown', dismissEscape);
      window.addEventListener('resize', syncSearchViewport, { passive: true });
      window.addEventListener('scroll', syncSearchViewport, { passive: true });
      window.visualViewport?.addEventListener('resize', syncSearchViewport);
      window.visualViewport?.addEventListener('scroll', syncSearchViewport);
      const cleanupSearch = (event) => {
        if (!event.target.contains(dialog)) return;
        document.removeEventListener('pointerdown', dismissOutside);
        document.removeEventListener('keydown', dismissEscape);
        window.removeEventListener('resize', syncSearchViewport);
        window.removeEventListener('scroll', syncSearchViewport);
        window.visualViewport?.removeEventListener('resize', syncSearchViewport);
        window.visualViewport?.removeEventListener('scroll', syncSearchViewport);
        window.clearTimeout(searchTimer);
        searchController?.abort();
        document.removeEventListener('shopify:section:unload', cleanupSearch);
      };
      document.addEventListener('shopify:section:unload', cleanupSearch);

      if (input) {
        input.addEventListener('input', () => {
          scheduleSearch();
        });
      }

      if (form) {
        form.addEventListener('submit', () => {
          closeSearch();
        });
      }
    });
  }

  function initQuickAdd(scope = document) {
    function resetQuickAdd(rootNode) {
      const section = rootNode ? rootNode.querySelector('[data-product-section-id]') : null;

      if (section) {
        section.dispatchEvent(new CustomEvent('ks:quick-add-reset'));
      }
    }

    function setQuickAddOpen(rootNode, isOpen) {
      if (!rootNode) return;

      rootNode.dataset.open = isOpen ? 'true' : 'false';

      const trigger = rootNode.querySelector('[data-quick-add-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }

      if (!isOpen) {
        resetQuickAdd(rootNode);
      }
    }

    collect(scope, '[data-quick-add]').forEach((quickAdd) => {
      if (quickAdd.dataset.quickAddInitialized === 'true') return;
      quickAdd.dataset.quickAddInitialized = 'true';

      const supportsHoverQuickAdd = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      const usesMobileNativeQuickAdd = quickAdd.dataset.quickAddMobileNativeEnabled === 'true' && !supportsHoverQuickAdd;
      const trigger = quickAdd.querySelector('[data-quick-add-trigger]');
      const panel = quickAdd.querySelector('[data-quick-add-panel]');
      const shield = quickAdd.querySelector('[data-quick-add-shield]');
      const productCard = quickAdd.closest('.product-card');
      const optionShells = collect(quickAdd, '[data-quick-add-options-shell]');

      optionShells.forEach((optionShell) => {
        const values = optionShell.querySelector('.ks-product-options__values');
        const previousButton = optionShell.querySelector('[data-quick-add-scroll-previous]');
        const nextButton = optionShell.querySelector('[data-quick-add-scroll-next]');

        if (!values || !previousButton || !nextButton) return;

        const updateOverflowState = () => {
          const maxScrollLeft = Math.max(0, values.scrollWidth - values.clientWidth);
          const isOverflowing = maxScrollLeft > 1;
          const canScrollPrevious = isOverflowing && values.scrollLeft > 1;
          const canScrollNext = isOverflowing && values.scrollLeft < maxScrollLeft - 1;

          optionShell.classList.toggle('is-overflowing', isOverflowing);
          previousButton.hidden = !canScrollPrevious;
          previousButton.disabled = !canScrollPrevious;
          nextButton.hidden = !canScrollNext;
          nextButton.disabled = !canScrollNext;
        };

        const scrollOptions = (direction) => {
          values.scrollBy({
            behavior: 'smooth',
            left: direction * Math.max(96, values.clientWidth * 0.72),
          });
        };

        previousButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollOptions(-1);
        });

        nextButton.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          scrollOptions(1);
        });

        values.addEventListener('scroll', updateOverflowState, { passive: true });

        if (typeof ResizeObserver === 'function') {
          const overflowObserver = new ResizeObserver(updateOverflowState);
          overflowObserver.observe(values);
        }

        window.requestAnimationFrame(updateOverflowState);
      });

      if (usesMobileNativeQuickAdd) {
        return;
      }

      quickAdd.addEventListener('pointerdown', (event) => {
        event.stopPropagation();
      }, { capture: true });

      quickAdd.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      if (!trigger || !panel) return;

      if (shield) {
        shield.addEventListener('pointerdown', (event) => {
          event.preventDefault();
          event.stopPropagation();
        });

        shield.addEventListener('click', (event) => {
          if (trigger.disabled) return;

          event.preventDefault();
          event.stopPropagation();
          setQuickAddOpen(quickAdd, true);
        });
      }

      if (supportsHoverQuickAdd) {
        trigger.addEventListener('mouseenter', () => {
          if (trigger.disabled) return;
          setQuickAddOpen(quickAdd, true);
        });
      }

      trigger.addEventListener('click', (event) => {
        if (trigger.disabled) return;

        event.preventDefault();
        event.stopPropagation();
        setQuickAddOpen(quickAdd, quickAdd.dataset.open !== 'true');
      });

      if (productCard && supportsHoverQuickAdd) {
        productCard.addEventListener('mouseleave', () => {
          setQuickAddOpen(quickAdd, false);
        });
      }

      quickAdd.addEventListener('focusin', () => {
        if (trigger.disabled || usesMobileNativeQuickAdd) return;
        setQuickAddOpen(quickAdd, true);
      });

      quickAdd.addEventListener('focusout', () => {
        if (usesMobileNativeQuickAdd) return;

        window.setTimeout(() => {
          if (quickAdd.contains(document.activeElement)) return;
          if (supportsHoverQuickAdd && quickAdd.matches(':hover')) return;
          setQuickAddOpen(quickAdd, false);
        }, 0);
      });

      quickAdd.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        setQuickAddOpen(quickAdd, false);
        trigger.focus();
      });
    });

    if (document.body.dataset.quickAddDocumentBound !== 'true') {
      document.body.dataset.quickAddDocumentBound = 'true';

      document.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const trigger = target?.closest('[data-quick-add-trigger]');

        if (!trigger || trigger.disabled) return;

        const quickAdd = trigger.closest('[data-quick-add]');
        if (!quickAdd) return;

        event.preventDefault();
        event.stopPropagation();
        setQuickAddOpen(quickAdd, quickAdd.dataset.open !== 'true');
      }, true);

      document.addEventListener('click', (event) => {
        collect(document, '[data-quick-add][data-open="true"]').forEach((quickAdd) => {
          if (quickAdd.contains(event.target)) return;
          setQuickAddOpen(quickAdd, false);
        });
      });
    }
  }

  async function loadProductCardBody(card, handle) {
    const productUrl = buildStoreProductUrl(handle);
    const sectionId = String(card.dataset.productCardBodySectionId || 'product-card-body-fragment').trim();
    const cardInstance = String(card.dataset.productCardInstance || '').trim();

    if (!productUrl || !sectionId || !cardInstance) {
      throw new Error('Unable to load this product card.');
    }

    const markup = await fetchSectionMarkup(productUrl, sectionId);
    const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
    const fragment = parsedDocument.querySelector('[data-product-card-body-fragment]');

    if (!fragment) {
      throw new Error('Unable to load this product card.');
    }

    return fragment.innerHTML.split('CARD_INSTANCE').join(cardInstance);
  }

  function initProductCardSwatches(scope = document) {
    collect(scope, '[data-product-card]').forEach((card) => {
      if (card.dataset.productCardInitialized === 'true') return;
      card.dataset.productCardInitialized = 'true';

      const prefetchSwatch = (event) => {
        const swatch = event.target.closest('[data-product-card-swatch]');
        const nextHandle = swatch?.dataset.productCardSwatch;

        if (!swatch || !card.contains(swatch) || !nextHandle || card.dataset.productCardHandle === nextHandle) return;

        loadProductCardBody(card, nextHandle).catch(() => {});
      };

      card.addEventListener('pointerover', prefetchSwatch, { passive: true });
      card.addEventListener('focusin', prefetchSwatch);

      card.addEventListener('click', async (event) => {
        const swatch = event.target.closest('[data-product-card-swatch]');
        if (!swatch || !card.contains(swatch)) return;

        const nextHandle = swatch.dataset.productCardSwatch;
        const body = card.querySelector('[data-product-card-body]');
        const previousSwatchOrder = body
          ? Array.from(body.querySelectorAll('[data-product-card-swatch]'))
            .map((item) => item.dataset.productCardSwatch)
            .filter(Boolean)
          : [];

        if (!nextHandle || !body) return;

        event.preventDefault();

        if (card.dataset.productCardHandle === nextHandle) return;

        const requestId = `${Date.now()}-${Math.random()}`;
        card.dataset.productCardRequest = requestId;
        card.dataset.productCardLoading = 'true';
        card.setAttribute('aria-busy', 'true');

        try {
          const nextBodyMarkup = await loadProductCardBody(card, nextHandle);

          if (card.dataset.productCardRequest !== requestId) return;

          body.innerHTML = nextBodyMarkup;
          card.dataset.productCardHandle = nextHandle;

          if (previousSwatchOrder.length > 0) {
            const swatchRow = body.querySelector('.product-card__option-values--combined');

            if (swatchRow) {
              const swatches = Array.from(swatchRow.querySelectorAll('[data-product-card-swatch]'));
              const swatchMap = new Map(
                swatches.map((item) => [item.dataset.productCardSwatch, item])
              );

              previousSwatchOrder.forEach((handle) => {
                const item = swatchMap.get(handle);
                if (!item) return;

                swatchRow.appendChild(item);
                swatchMap.delete(handle);
              });

              swatchMap.forEach((item) => {
                swatchRow.appendChild(item);
              });
            }
          }

          initScope(body);

          const activeSwatch = body.querySelector(`[data-product-card-swatch="${nextHandle}"]`);
          if (activeSwatch) {
            activeSwatch.focus({ preventScroll: true });
          }
        } catch (error) {
          if (card.dataset.productCardRequest === requestId && swatch.href) {
            window.location.assign(swatch.href);
          }
        } finally {
          if (card.dataset.productCardRequest === requestId) {
            delete card.dataset.productCardRequest;
            delete card.dataset.productCardLoading;
            card.removeAttribute('aria-busy');
          }
        }
      });
    });
  }

  let nativeCartBusy = false;
  const nativeAddonCache = new Map();

  function initNativeCart(scope = document) {
    collect(scope, '[data-native-cart-root]').forEach((drawer) => {
      const root = drawer.dataset.cartRoot || '/';
      const endpoint = (path) => root + path;
      const status = (message) => {
        const node = drawer.querySelector('[data-nc-status]');
        if (node) node.textContent = message;
        if (message) announce(message);
      };
      const request = async (path, payload) => {
        const response = await fetch(endpoint(path), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to update cart.');
        return data;
      };
      const readCart = async () => {
        const response = await fetch(endpoint('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) throw new Error('Unable to load cart.');
        return response.json();
      };
      const mutate = async (work, focusSelector) => {
        if (nativeCartBusy) return;
        nativeCartBusy = true;
        status('');
        drawer.setAttribute('aria-busy', 'true');
        drawer.querySelectorAll('button:not([data-dialog-close]),input,select,textarea').forEach((el) => {
          if (!el.disabled) { el.dataset.ncLocked = 'true'; el.disabled = true; }
        });
        const previousScroll = drawer.querySelector('.nc-body')?.scrollTop || 0;
        try {
          const message = await work();
          const cart = await readCart();
          updateCartCount(cart.item_count);
          await refreshHeaderCartState({ openDrawer: true });
          const bodyNode = drawer.querySelector('.nc-body');
          if (bodyNode) bodyNode.scrollTop = previousScroll;
          if (/not valid|unavailable/i.test(message || '')) status(message);
          else announce(message || 'Cart updated.');
          const focusTarget = (focusSelector && drawer.querySelector(focusSelector)) || drawer.querySelector('[data-dialog-close]');
          focusTarget?.focus({ preventScroll: true });
        } catch (error) {
          try {
            const cart = await readCart();
            updateCartCount(cart.item_count);
            await refreshHeaderCartState();
          } catch (_) {}
          status(error.message || 'Unable to update cart. Please try again.');
          drawer.querySelector(focusSelector || '[data-dialog-close]')?.focus({ preventScroll: true });
        } finally {
          nativeCartBusy = false;
          drawer.removeAttribute('aria-busy');
          drawer.querySelectorAll('[data-nc-locked]').forEach((el) => { el.disabled = false; delete el.dataset.ncLocked; });
        }
      };
      drawer.querySelectorAll('[data-nc-change]').forEach((button) => {
        if (button.dataset.ncBound) return;
        button.dataset.ncBound = 'true';
        button.addEventListener('click', () => {
          const id = button.dataset.ncChange;
          const quantity = Number(button.dataset.quantity);
          mutate(() => request('cart/change.js', { id, quantity }).then(() => ''), '[data-nc-quantity="' + CSS.escape(id) + '"]');
        });
      });
      drawer.querySelectorAll('[data-nc-quantity]').forEach((input) => {
        if (input.dataset.ncBound) return;
        input.dataset.ncBound = 'true';
        input.addEventListener('change', () => {
          const quantity = Number(input.value);
          if (!Number.isInteger(quantity) || quantity < 0) { status('Enter a whole quantity of zero or more.'); return; }
          const id = input.dataset.ncQuantity;
          mutate(() => request('cart/change.js', { id, quantity }).then(() => ''), '[data-nc-quantity="' + CSS.escape(id) + '"]');
        });
      });
      const track = drawer.querySelector('[data-nc-track]');
      if (track && !track.dataset.ncBound) {
        track.dataset.ncBound = 'true';
        const previous = drawer.querySelector('[data-nc-prev]');
        const next = drawer.querySelector('[data-nc-next]');
        const update = () => {
          previous.disabled = track.scrollLeft < 2;
          next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        };
        previous.addEventListener('click', () => track.scrollBy({ left: -(track.clientWidth + 12), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }));
        next.addEventListener('click', () => track.scrollBy({ left: track.clientWidth + 12, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }));
        track.addEventListener('scroll', update, { passive: true });
        if (drawer._ncResize) drawer._ncResize.disconnect();
        drawer._ncResize = new ResizeObserver(update);
        drawer._ncResize.observe(track);
        update();
      }
      drawer.querySelectorAll('[data-nc-upsell]').forEach((card) => {
        if (card.dataset.ncBound) return;
        card.dataset.ncBound = 'true';
        const select = card.querySelector('[data-nc-variant]');
        select.addEventListener('change', () => {
          const option = select.selectedOptions[0];
          const price = card.querySelector('[data-nc-upsell-price]');
          price.textContent = option.dataset.price;
          if (option.dataset.compare) {
            const compare = document.createElement('s');
            compare.textContent = option.dataset.compare;
            price.append(' ', compare);
          }
        });
        card.querySelector('[data-nc-add]').addEventListener('click', () => {
          const id = Number(select.value);
          if (!id || select.selectedOptions[0]?.disabled) return;
          const item = { id, quantity: 1 };
          if (card.dataset.finalSale === 'true') item.properties = { 'Final sale': 'No returns or exchanges' };
          mutate(() => request('cart/add.js', { items: [item] }).then(() => 'Added to your cart.'), '[data-nc-next]');
        });
      });
      const addon = drawer.querySelector('[data-nc-addon]');
      if (addon && !addon.dataset.ncBound) {
        addon.dataset.ncBound = 'true';
        const id = Number(addon.dataset.variantId);
        const toggle = addon.querySelector('[data-nc-addon-toggle]');
        if (!nativeAddonCache.has(id)) {
          nativeAddonCache.set(id, fetch(endpoint('variants/' + id + '.js'), { headers: { Accept: 'application/json' } })
            .then((response) => { if (!response.ok) throw new Error('Protection is unavailable.'); return response.json(); })
            .catch((error) => { nativeAddonCache.delete(id); throw error; }));
        }
        nativeAddonCache.get(id).then((variant) => {
          if (!addon.isConnected) return;
          addon.querySelector('[data-nc-addon-price]').textContent = new Intl.NumberFormat(body.dataset.currencyLocale || 'en', { style: 'currency', currency: body.dataset.currencyCode || 'USD' }).format(variant.price / 100);
          toggle.disabled = !variant.available && addon.dataset.addonSelected !== 'true';
        }).catch(() => { if (addon.isConnected) status('Protection is currently unavailable. You can still check out.'); });
        toggle.addEventListener('click', () => mutate(async () => {
          const cart = await readCart();
          const lines = cart.items.filter((item) => item.variant_id === id);
          if (lines.length) {
            for (const line of lines) await request('cart/change.js', { id: line.key, quantity: 0 });
          } else {
            await request('cart/add.js', { items: [{ id, quantity: 1 }] });
          }
          await new Promise((resolve) => window.setTimeout(resolve, 3000));
          const verified = await readCart();
          const isPresent = verified.items.some((item) => item.variant_id === id);
          if (isPresent !== !lines.length) {
            throw new Error('The protection provider changed this selection. Protection has not been confirmed. Please contact us if you need help.');
          }
          return lines.length ? 'Protection removed.' : 'Protection added.';
        }, '[data-nc-addon-toggle]'));
      }
      const discount = drawer.querySelector('[data-nc-discount]');
      if (discount && !discount.dataset.ncBound) {
        discount.dataset.ncBound = 'true';
        const apply = async () => {
          const code = discount.value.trim();
          if (!code) { status('Enter a discount code.'); return; }
          await mutate(async () => {
            const cart = await readCart();
            const codes = (cart.discount_codes || []).filter((entry) => entry.applicable).map((entry) => entry.code);
            const unique = [...new Set([...codes, code].map((value) => value.toUpperCase()))];
            const updated = await request('cart/update.js', { discount: unique.join(',') });
            const applied = (updated.discount_codes || []).find((entry) => entry.code.toLowerCase() === code.toLowerCase());
            return applied?.applicable ? 'Discount applied.' : 'This discount is not valid for the items in your cart.';
          }, '[data-nc-discount]');
        };
        drawer.querySelector('[data-nc-apply]').addEventListener('click', apply);
        discount.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); apply(); } });
        readCart().then((cart) => {
          const target = drawer.querySelector('[data-nc-discounts]');
          if (!target || !discount.isConnected) return;
          for (const entry of (cart.discount_codes || []).filter((value) => value.applicable)) {
            const remove = document.createElement('button');
            remove.type = 'button';
            remove.textContent = entry.code + ' ×';
            remove.setAttribute('aria-label', 'Remove discount ' + entry.code);
            remove.addEventListener('click', () => mutate(async () => {
              const latest = await readCart();
              await request('cart/update.js', { discount: (latest.discount_codes || []).filter((value) => value.applicable && value.code !== entry.code).map((value) => value.code).join(',') });
              return 'Discount removed.';
            }, '[data-nc-discount]'));
            target.append(remove);
          }
        }).catch(() => {});
      }
      const checkout = drawer.querySelector('.nc-checkout-form');
      if (checkout && !checkout.dataset.ncBound) {
        checkout.dataset.ncBound = 'true';
        checkout.addEventListener('submit', async (event) => {
          if (nativeCartBusy) { event.preventDefault(); return; }
          const note = drawer.querySelector('[data-nc-note]');
          if (!note || checkout.dataset.noteSaved) return;
          event.preventDefault();
          try {
            await request('cart/update.js', { note: note.value });
            checkout.dataset.noteSaved = 'true';
            checkout.requestSubmit(checkout.querySelector('[name="checkout"]'));
          } catch (error) { status(error.message); }
        });
      }
    });
  }

  function initCartDrawer(scope = document) {
    initNativeCart(scope);
    collect(scope, '[data-cart-change-line]').forEach((button) => {
      if (button.dataset.cartChangeInitialized === 'true') return;
      button.dataset.cartChangeInitialized = 'true';

      button.addEventListener('click', async () => {
        const line = Number(button.dataset.cartChangeLine);
        const quantity = Number(button.dataset.cartChangeQuantity);

        if (!line || Number.isNaN(quantity) || quantity < 0) return;

        button.disabled = true;

        try {
          const response = await fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ line, quantity }),
          });

          const cart = await response.json();

          if (!response.ok) {
            throw new Error(cart.description || 'Unable to update cart.');
          }

          updateCartCount(cart.item_count);
          await refreshHeaderCartState({ openDrawer: true });
        } catch (error) {
          announce(error.message || 'Unable to update cart.');
        } finally {
          button.disabled = false;
        }
      });
    });
  }

  function initAnimations(scope = document) {
    const animationStyle = body.dataset.animationStyle;
    const animatedNodes = collect(scope, '[data-animate]');

    if (animatedNodes.length === 0) return;

    if (!animationStyle || animationStyle === 'none') {
      animatedNodes.forEach((node) => {
        animationObserver?.unobserve(node);
        node.dataset.animateInitialized = 'true';
        node.classList.add('is-visible');
      });
      return;
    }

    const mainContent = document.querySelector('#MainContent');
    const firstMainSection = Array.from(mainContent?.children || []).find((child) => (
      child.classList.contains('shopify-section')
    )) || mainContent?.firstElementChild;
    const firstSectionEntrance = firstMainSection?.matches('[data-animate]')
      ? firstMainSection
      : firstMainSection?.querySelector(':scope > [data-animate]');
    const shouldRemainVisible = (node) => (
      node === firstSectionEntrance
      || Boolean(node.closest('.shopify-section-group-header-group, header, .announcement-bar'))
    );

    const revealNodes = animatedNodes.filter((node) => {
      if (!shouldRemainVisible(node)) return true;

      animationObserver?.unobserve(node);
      node.dataset.animateInitialized = 'true';
      node.classList.add('is-visible');
      return false;
    });

    if (revealNodes.length === 0) return;

    if (!animationObserver) {
      animationObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            entry.target.classList.add('is-visible');
            animationObserver.unobserve(entry.target);
          });
        },
        {
          rootMargin: '0px 0px -10% 0px',
          threshold: 0.12,
        }
      );
    }

    revealNodes.forEach((node) => {
      if (node.dataset.animateInitialized === 'true') return;
      node.dataset.animateInitialized = 'true';

      const bounds = node.getBoundingClientRect();
      const isAlreadyVisible = (
        node.hasAttribute('data-animate-immediate')
        || (bounds.top < window.innerHeight && bounds.bottom > 0)
      );

      if (isAlreadyVisible) {
        node.classList.add('is-visible');
        return;
      }

      animationObserver.observe(node);
    });
  }

  let imageRevealObserver = null;
  let imageRevealMutationObserver = null;
  const imageRevealStates = new WeakMap();

  function getImageRevealSurface(image) {
    if (!(image instanceof HTMLImageElement)) return null;

    const responsiveAsset = image.closest('.ks-responsive-media__asset');

    if (
      image.classList.contains('product-card__image--secondary')
      || image.classList.contains('ks-product-media__image')
      || image.classList.contains('ks-header__logo')
      || image.classList.contains('ks-header__logo-svg')
      || image.classList.contains('ks-header__drawer-logo-image')
      || (responsiveAsset && window.getComputedStyle(responsiveAsset).display === 'none')
      || image.closest('[data-media-fade]')
    ) {
      return null;
    }

    return image.closest('.product-card')
      || image.closest('.ks-image-card')
      || image;
  }

  function revealImageState(state) {
    if (!state || state.revealed || !state.loaded || !state.inView) return;

    state.revealed = true;

    const animatedAncestorOpacity = state.animatedAncestor
      ? Number.parseFloat(window.getComputedStyle(state.animatedAncestor).opacity)
      : 1;
    const ancestorIsHandlingReveal = (
      state.animatedAncestor
      && Number.isFinite(animatedAncestorOpacity)
      && animatedAncestorOpacity < 0.99
    );

    if (ancestorIsHandlingReveal) {
      state.surface.classList.add('is-image-revealed', 'is-image-revealed--with-ancestor');
      imageRevealObserver?.unobserve(state.surface);
      return;
    }

    window.requestAnimationFrame(() => {
      state.surface.classList.add('is-image-revealed');
    });
    imageRevealObserver?.unobserve(state.surface);
  }

  function prepareImageReveal(image) {
    if (!(image instanceof HTMLImageElement)) return;
    if (image.hasAttribute('data-deferred-image') && image.dataset.deferredImageLoaded !== 'true') return;

    const surface = getImageRevealSurface(image);

    if (!surface || surface.dataset.imageRevealInitialized === 'true') return;

    surface.dataset.imageRevealInitialized = 'true';
    surface.setAttribute('data-image-reveal', '');

    const state = {
      animatedAncestor: surface.closest('[data-animate]'),
      image,
      inView: false,
      loaded: image.complete,
      revealed: false,
      surface,
    };

    imageRevealStates.set(surface, state);

    const markLoaded = () => {
      state.loaded = true;
      revealImageState(state);
    };

    if (!image.complete) {
      image.addEventListener('load', markLoaded, { once: true });
      image.addEventListener('error', markLoaded, { once: true });
    }

    if (typeof IntersectionObserver !== 'function') {
      state.inView = true;
      revealImageState(state);
      return;
    }

    if (!imageRevealObserver) {
      imageRevealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const entryState = imageRevealStates.get(entry.target);

            if (!entryState || !entry.isIntersecting) return;

            entryState.inView = true;
            revealImageState(entryState);
          });
        },
        {
          rootMargin: '120px 1200px',
          threshold: 0.01,
        }
      );
    }

    imageRevealObserver.observe(surface);
  }

  function initImageReveals(scope = document) {
    collect(scope, 'img').forEach((image) => {
      prepareImageReveal(image);
    });

    if (!imageRevealMutationObserver && document.body && typeof MutationObserver === 'function') {
      imageRevealMutationObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;

            collect(node, 'img').forEach((image) => {
              prepareImageReveal(image);
            });
          });
        });
      });
      imageRevealMutationObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  function initMediaFades(scope = document) {
    collect(scope, '[data-media-fade]').forEach((surface) => {
      if (surface.dataset.mediaFadeInitialized === 'true') return;
      surface.dataset.mediaFadeInitialized = 'true';

      const candidates = Array.from(surface.querySelectorAll('img, video'));
      const visibleMedia = candidates.find((media) => {
        const asset = media.closest('.ks-responsive-media__asset');
        return !asset || window.getComputedStyle(asset).display !== 'none';
      }) || candidates[0];

      const reveal = () => {
        window.requestAnimationFrame(() => {
          surface.classList.add('is-media-loaded');
        });
      };

      if (!visibleMedia) {
        reveal();
        return;
      }

      if (visibleMedia.tagName === 'IMG') {
        if (visibleMedia.complete) {
          reveal();
        } else {
          visibleMedia.addEventListener('load', reveal, { once: true });
          visibleMedia.addEventListener('error', reveal, { once: true });
        }
        return;
      }

      if (visibleMedia.readyState >= 2) {
        reveal();
      } else {
        visibleMedia.addEventListener('loadeddata', reveal, { once: true });
        visibleMedia.addEventListener('error', reveal, { once: true });
      }
    });
  }

  let deferredImageObserver = null;
  let deferredImageVisibilityObserver = null;
  let deferredImageVisibilityFrame = null;

  function isDeferredImageVisible(image) {
    if (!(image instanceof HTMLImageElement) || image.hidden || image.getClientRects().length === 0) return false;

    const styles = window.getComputedStyle(image);

    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  function hydrateDeferredImage(image) {
    if (!(image instanceof HTMLImageElement) || image.dataset.deferredImageLoaded === 'true') return;

    const source = String(image.dataset.deferredSrc || '').trim();
    const sourceSet = String(image.dataset.deferredSrcset || '').trim();

    if (sourceSet) image.srcset = sourceSet;
    if (source) image.src = source;

    image.dataset.deferredImageLoaded = 'true';
    image.removeAttribute('data-deferred-src');
    image.removeAttribute('data-deferred-srcset');
    deferredImageObserver?.unobserve(image);
    prepareImageReveal(image);
  }

  function hydrateVisibleDeferredImages(scope = document) {
    collect(scope, 'img[data-deferred-image]').forEach((image) => {
      if (isDeferredImageVisible(image)) hydrateDeferredImage(image);
    });
  }

  let secondaryImageObserver;
  function initProductCardSecondaryImages(scope = document) {
    const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
    const hydrate = (image) => {
      if (!hoverMedia.matches || image.dataset.productCardSecondaryLoaded) return;
      if (image.dataset.productCardSecondarySrcset) image.srcset = image.dataset.productCardSecondarySrcset;
      if (image.dataset.productCardSecondarySrc) image.src = image.dataset.productCardSecondarySrc;
      image.dataset.productCardSecondaryLoaded = 'true';
      image.removeAttribute('data-product-card-secondary-srcset');
      image.removeAttribute('data-product-card-secondary-src');
      secondaryImageObserver?.unobserve(image.closest('.product-card') || image);
    };
    if (!secondaryImageObserver && 'IntersectionObserver' in window) {
      secondaryImageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const image = entry.target.matches('[data-product-card-secondary-image]') ? entry.target : entry.target.querySelector('[data-product-card-secondary-image]');
            if (image) hydrate(image);
          }
        });
      }, { rootMargin: '240px' });
    }
    collect(scope, '[data-product-card-secondary-image]').forEach((image) => {
      if (image.dataset.secondaryInitialized) return;
      image.dataset.secondaryInitialized = 'true';
      const card = image.closest('.product-card') || image;
      card.addEventListener('pointerenter', () => hydrate(image), { passive: true });
      card.addEventListener('focusin', () => hydrate(image));
      if (hoverMedia.matches) {
        if (secondaryImageObserver) secondaryImageObserver.observe(card);
        else hydrate(image);
      }
    });
  }

  function initDeferredImages(scope = document) {
    const images = collect(scope, 'img[data-deferred-image]');

    if (images.length === 0) return;

    if (typeof IntersectionObserver !== 'function') {
      hydrateVisibleDeferredImages(scope);
      return;
    }

    if (!deferredImageObserver) {
      deferredImageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && isDeferredImageVisible(entry.target)) {
            hydrateDeferredImage(entry.target);
          }
        });
      }, {
        rootMargin: '160px',
      });
    }

    images.forEach((image) => deferredImageObserver.observe(image));

    if (!deferredImageVisibilityObserver && typeof MutationObserver === 'function') {
      const header = document.querySelector('[data-header-section-id]');

      if (header) {
        deferredImageVisibilityObserver = new MutationObserver(() => {
          window.cancelAnimationFrame(deferredImageVisibilityFrame);
          deferredImageVisibilityFrame = window.requestAnimationFrame(() => {
            hydrateVisibleDeferredImages(header);
          });
        });
        deferredImageVisibilityObserver.observe(header, {
          attributeFilter: ['aria-hidden', 'class', 'data-nav-open', 'data-open', 'hidden'],
          attributes: true,
          subtree: true,
        });
      }
    }
  }

  function warmHorizontalProductRailImages(rail) {
    if (!(rail instanceof Element) || rail.clientWidth <= 0) return;

    const railStyles = window.getComputedStyle(rail);
    const canScrollHorizontally = (
      ['auto', 'scroll'].includes(railStyles.overflowX)
      && rail.scrollWidth > rail.clientWidth + 4
    );

    if (!canScrollHorizontally) return;

    const preloadEnd = rail.scrollLeft + Math.max(rail.clientWidth * 3, 1200);

    collect(rail, '.product-card').forEach((card) => {
      if (card.offsetLeft > preloadEnd) return;

      collect(card, 'img:not(.product-card__image--secondary)').forEach((image) => {
        image.loading = 'eager';

        if (image.hasAttribute('data-deferred-image')) {
          hydrateDeferredImage(image);
        }

        prepareImageReveal(image);

        const surface = getImageRevealSurface(image);
        const revealState = surface ? imageRevealStates.get(surface) : null;

        if (revealState) {
          revealState.inView = true;
          revealImageState(revealState);
        }
      });
    });
  }

  function initHorizontalProductImagePreloading(scope = document) {
    const selector = '.featured-collection__grid--scroll-desktop, .featured-collection__grid--scroll-mobile';

    collect(scope, selector).forEach((rail) => {
      if (rail.dataset.horizontalImagePreloadingInitialized === 'true') {
        rail.__ksWarmHorizontalProductImages?.();
        return;
      }

      rail.dataset.horizontalImagePreloadingInitialized = 'true';
      let preloadFrame = 0;
      const schedulePreload = () => {
        window.cancelAnimationFrame(preloadFrame);
        preloadFrame = window.requestAnimationFrame(() => warmHorizontalProductRailImages(rail));
      };

      rail.__ksWarmHorizontalProductImages = schedulePreload;
      rail.addEventListener('scroll', schedulePreload, { passive: true });

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(schedulePreload);
        resizeObserver.observe(rail);
        rail.__ksHorizontalImageResizeObserver = resizeObserver;
      }

      if (typeof MutationObserver === 'function') {
        const mutationObserver = new MutationObserver(schedulePreload);
        mutationObserver.observe(rail, { childList: true, subtree: true });
        rail.__ksHorizontalImageMutationObserver = mutationObserver;
      }

      schedulePreload();
    });
  }

  function initStickyHeaders(scope = document) {
    collect(scope, '[data-sticky-mode]').forEach((header) => {
      if (header.dataset.stickyInitialized === 'true') return;
      header.dataset.stickyInitialized = 'true';

      const wrapper = header.closest('.shopify-section');
      const stickyMode = String(header.dataset.stickyMode || 'none');
      const isTransparent = header.classList.contains('ks-header--transparent');
      let previousY = window.scrollY;
      let stickyStart = 0;
      let headerHidden = false;
      let headerHeight = 0;
      let forceSolid = false;
      let directionDistance = 0;

      if (wrapper) {
        wrapper.classList.add('ks-header-section');
        wrapper.classList.toggle('ks-header-section--sticky', stickyMode !== 'none');
        wrapper.classList.toggle('ks-header-section--overlay', isTransparent);
      }

      const update = () => {
        const currentY = window.scrollY;
        const scrollDelta = currentY - previousY;
        const overlayOpen = Boolean(header.querySelector('dialog[open]')) || header.dataset.mobileDrawerOpen === 'true';
        directionDistance = Math.sign(scrollDelta) === Math.sign(directionDistance) ? directionDistance + scrollDelta : scrollDelta;
        const transparentThreshold = isTransparent ? 4 : Math.max(1, stickyStart);
        const isPastStickyStart = currentY > transparentThreshold;

        if (stickyMode === 'scroll_up') {
          if (currentY <= 1 || directionDistance < -8 || overlayOpen) {
            headerHidden = false;
          } else if (directionDistance > 8 && currentY > Math.max(120, stickyStart + 120)) {
            headerHidden = true;
          }
        } else {
          headerHidden = false;
        }

        if (overlayOpen) directionDistance = 0;
        header.classList.toggle('is-scrolled', isTransparent && (isPastStickyStart || forceSolid || overlayOpen));
        header.classList.toggle('is-scrolled-down', headerHidden);
        document.documentElement.style.setProperty(
          '--theme-sticky-header-offset',
          `${stickyMode !== 'none' && !headerHidden ? headerHeight : 0}px`,
        );
        previousY = currentY;
      };

      const measure = () => {
        headerHeight = header.getBoundingClientRect().height;

        if (wrapper) {
          wrapper.style.setProperty('--ks-header-overlay-height', `${headerHeight}px`);
          stickyStart = wrapper.offsetTop;
        }

        update();
      };

      const finishInitialState = () => {
        header.classList.remove('ks-header--initializing');
      };

      if (isTransparent) {
        header.addEventListener('mouseenter', () => {
          if (!desktopHoverMedia.matches) return;
          forceSolid = true;
          update();
        });

        header.addEventListener('mouseleave', () => {
          if (!desktopHoverMedia.matches) return;
          forceSolid = false;
          update();
        });
      }

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', measure, { passive: true });
      window.addEventListener('load', measure, { passive: true, once: true });
      window.addEventListener('pageshow', measure, { passive: true });

      if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(header);
        header.__ksStickyResizeObserver = resizeObserver;
      }

      measure();
      requestAnimationFrame(() => {
        measure();
        requestAnimationFrame(finishInitialState);
      });
      window.setTimeout(measure, 120);
      window.setTimeout(measure, 320);
    });
  }

  function getNewsletterKlaviyoSettings() {
    return {
      enabled: body.dataset.klaviyoNewsletterEnabled === 'true',
      publicKey: String(body.dataset.klaviyoNewsletterPublicKey || '').trim(),
      listId: String(body.dataset.klaviyoNewsletterListId || '').trim(),
    };
  }

  function isNewsletterCustomerForm(form) {
    if (!form || form.dataset.klaviyoNewsletterInitialized === 'true') return false;

    const emailInput = form.querySelector('input[name="contact[email]"]');
    if (!emailInput) return false;

    const tagInput = form.querySelector('input[name="contact[tags]"]');
    const tags = String(tagInput?.value || '').toLowerCase();

    return (
      form.classList.contains('ks-newsletter__form')
      || form.classList.contains('ks-footer__newsletter-form')
      || tags.split(',').map((tag) => tag.trim()).includes('newsletter')
    );
  }

  function buildKlaviyoNewsletterPayload(form, settings) {
    const email = form.querySelector('input[name="contact[email]"]')?.value?.trim();
    const firstName = form.querySelector('input[name="contact[first_name]"]')?.value?.trim();
    const profileAttributes = {
      email,
      subscriptions: {
        email: {
          marketing: {
            consent: 'SUBSCRIBED',
          },
        },
      },
    };

    if (firstName) {
      profileAttributes.first_name = firstName;
    }

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Shopify theme newsletter',
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: settings.listId,
            },
          },
        },
      },
    };
  }

  async function syncKlaviyoNewsletterForm(form, settings) {
    const email = form.querySelector('input[name="contact[email]"]')?.value?.trim();

    if (!settings.enabled || !settings.publicKey || !settings.listId || !email) return;

    const endpoint = 'https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(settings.publicKey);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const timeout = window.setTimeout(() => {
      controller?.abort();
    }, 10000);

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/vnd.api+json',
          revision: '2026-04-15',
        },
        body: JSON.stringify(buildKlaviyoNewsletterPayload(form, settings)),
        keepalive: true,
        signal: controller?.signal,
      });

      if (!response.ok) {
        throw new Error('Klaviyo newsletter sync failed with ' + response.status);
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function initKlaviyoNewsletterForms(scope = document) {
    const settings = getNewsletterKlaviyoSettings();

    collect(scope, 'form').forEach((form) => {
      if (!isNewsletterCustomerForm(form)) return;
      if (!form.hasAttribute('data-klaviyo-direct')) return;

      form.dataset.klaviyoNewsletterInitialized = 'true';
      const buttons = Array.from(form.querySelectorAll('button[type="submit"]'));
      const feedback = form.querySelector('[data-newsletter-feedback]');
      const ready = settings.enabled && settings.publicKey && settings.listId;
      buttons.forEach((button) => { button.disabled = !ready; });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (!ready || form.dataset.newsletterBusy === 'true' || !form.reportValidity()) return;
        form.dataset.newsletterBusy = 'true';
        form.setAttribute('aria-busy', 'true');
        buttons.forEach((button) => { button.disabled = true; });
        if (feedback) {
          feedback.hidden = false;
          feedback.textContent = 'Signing you up…';
        }

        try {
          await syncKlaviyoNewsletterForm(form, settings);
          form.reset();
          if (feedback) feedback.textContent = form.dataset.successMessage || 'Thanks for signing up. Please check your inbox for any confirmation email.';
        } catch (error) {
          if (feedback) feedback.textContent = 'We couldn’t complete your signup. Please try again shortly.';
        } finally {
          delete form.dataset.newsletterBusy;
          form.removeAttribute('aria-busy');
          buttons.forEach((button) => { button.disabled = false; });
        }
      });
    });
  }
  function initAutoSubmit(scope = document) {
    collect(scope, '[data-auto-submit]').forEach((field) => {
      if (field.dataset.autoSubmitInitialized === 'true') return;
      field.dataset.autoSubmitInitialized = 'true';

      field.addEventListener('change', () => {
        if (field.form) field.form.submit();
      });
    });
  }

  function initCustomerAddressForms(scope = document) {
    collect(scope, '[data-address-country]').forEach((countrySelect) => {
      if (countrySelect.dataset.addressInitialized === 'true') return;
      countrySelect.dataset.addressInitialized = 'true';

      const form = countrySelect.closest('form');
      const provinceContainer = form?.querySelector('[data-address-province-container]');
      const provinceSelect = form?.querySelector('[data-address-province]');
      const defaultCountry = String(countrySelect.dataset.default || '').trim();
      const defaultProvince = String(provinceSelect?.dataset.default || '').trim();
      let appliedDefaultProvince = false;

      if (defaultCountry) {
        countrySelect.value = defaultCountry;
      }

      function updateProvinceOptions() {
        if (!provinceContainer || !provinceSelect) return;

        const selectedOption = countrySelect.options[countrySelect.selectedIndex];
        let provinces = [];

        try {
          provinces = JSON.parse(selectedOption?.dataset?.provinces || '[]');
        } catch (error) {
          provinces = [];
        }

        const currentProvinceValue = String(provinceSelect.value || '').trim();

        provinceSelect.innerHTML = provinces.map(([value, label]) => (
          `<option value="${escapeHtml(value)}">${escapeHtml(label)}</option>`
        )).join('');

        provinceContainer.hidden = provinces.length === 0;

        if (provinces.length === 0) {
          provinceSelect.value = '';
          return;
        }

        if (!appliedDefaultProvince && defaultProvince) {
          provinceSelect.value = defaultProvince;
          appliedDefaultProvince = true;
          return;
        }

        if (currentProvinceValue && provinces.some(([value]) => value === currentProvinceValue)) {
          provinceSelect.value = currentProvinceValue;
          return;
        }

        provinceSelect.value = provinces[0][0];
      }

      countrySelect.addEventListener('change', updateProvinceOptions);
      updateProvinceOptions();
    });
  }

  function initAjaxCart(scope = document) {
    if (body.dataset.ajaxCart !== 'true') return;

    const forms = collect(scope, '.ks-product__form, .product-card__form');

    forms.forEach((form) => {
      if (form.dataset.ajaxInitialized === 'true') return;
      form.dataset.ajaxInitialized = 'true';

      form.addEventListener('submit', async (event) => {
        const submitter = event.submitter;

        if (
          submitter
          && (submitter.name === 'checkout' || submitter.closest('.shopify-payment-button'))
        ) {
          return;
        }

        event.preventDefault();

        const button = submitter || form.querySelector('[type="submit"]');
        const buttonLabel = button
          ? button.querySelector('[data-add-to-cart-text], .product-card__quick-add-label')
          : null;
        const originalLabel = buttonLabel
          ? buttonLabel.textContent
          : (button ? button.textContent : '');
        const loadingDrawer = openCartDrawerLoading(form);
        const autoSubmitSection = form.closest('[data-auto-submit-variant="true"]');

        if (button) {
          button.disabled = true;
          if (buttonLabel) {
            buttonLabel.textContent = 'Adding...';
          } else {
            button.textContent = 'Adding...';
          }
        }

        try {
          const response = await fetch('/cart/add.js', {
            body: new FormData(form),
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            method: 'POST',
          });

          const payload = await response.json();

          if (!response.ok) {
            throw new Error(payload.description || 'Unable to add item to cart.');
          }

          const cart = await fetchCart();
          updateCartCount(cart.item_count);
          const parentQuickView = form.closest('[data-quick-view]');
          if (parentQuickView && typeof parentQuickView.close === 'function' && parentQuickView.open) {
            parentQuickView.close();
          }
          const parentQuickAdd = form.closest('[data-quick-add]');
          if (parentQuickAdd) {
            parentQuickAdd.dataset.open = 'false';

            const trigger = parentQuickAdd.querySelector('[data-quick-add-trigger]');
            if (trigger) {
              trigger.setAttribute('aria-expanded', 'false');
            }
          }
          if (autoSubmitSection) {
            autoSubmitSection.dispatchEvent(new CustomEvent('ks:quick-add-reset'));
          }
          await refreshHeaderCartState({ openDrawer: true });
          announce(`${payload.product_title} added to cart.`);
        } catch (error) {
          if (loadingDrawer) {
            delete loadingDrawer.dataset.cartLoading;
          }
          announce(error.message || 'Unable to add item to cart.');
        } finally {
          if (autoSubmitSection) {
            delete autoSubmitSection.dataset.variantSubmitting;
            delete autoSubmitSection.dataset.loading;
          }

          if (button) {
            button.disabled = false;
            if (buttonLabel) {
              buttonLabel.textContent = originalLabel;
            } else {
              button.textContent = originalLabel;
            }
          }
        }
      });
    });
  }

  function readLocalStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function writeLocalStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // Ignore storage failures in private browsing or restricted environments.
    }
  }

  function readRecentlyViewedProductHandles() {
    try {
      const storedValue = JSON.parse(readLocalStorage('ks-recently-viewed-products') || '[]');

      return Array.isArray(storedValue)
        ? storedValue.map((handle) => String(handle || '').trim()).filter(Boolean)
        : [];
    } catch (error) {
      return [];
    }
  }

  function recordRecentlyViewedProduct(handle) {
    const normalizedHandle = String(handle || '').trim();

    if (!normalizedHandle) return;

    const nextHandles = [
      normalizedHandle,
      ...readRecentlyViewedProductHandles().filter((storedHandle) => storedHandle !== normalizedHandle),
    ].slice(0, 16);

    writeLocalStorage('ks-recently-viewed-products', JSON.stringify(nextHandles));
  }

  function getRecentlyViewedProductHandles(currentHandle, limit = 4) {
    const normalizedCurrentHandle = String(currentHandle || '').trim();
    const normalizedLimit = Math.max(1, Number(limit) || 4);

    return readRecentlyViewedProductHandles()
      .filter((handle) => handle !== normalizedCurrentHandle)
      .slice(0, normalizedLimit);
  }

  function initProductRecommendations(scope = document) {
    collect(scope, '[data-product-recommendations-root]').forEach((section) => {
      if (section.dataset.productRecommendationsInitialized === 'true') return;
      section.dataset.productRecommendationsInitialized = 'true';

      if (section.dataset.productRecommendationsLoaded === 'true') {
        const hasResults = section.dataset.productRecommendationsHasResults === 'true';
        section.hidden = !hasResults;
        return;
      }

      const requestUrl = String(section.dataset.productRecommendationsUrl || '').trim();

      if (!requestUrl) {
        section.hidden = true;
        return;
      }

      section.dataset.productRecommendationsLoading = 'true';

      fetch(requestUrl, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Unable to load product recommendations.');
          }

          return response.text();
        })
        .then((markup) => {
          const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
          const replacement = parsedDocument.querySelector('[data-product-recommendations-root]');

          if (!replacement) {
            throw new Error('Unable to load product recommendations.');
          }

          const hasResults = replacement.dataset.productRecommendationsHasResults === 'true';

          if (!hasResults) {
            section.hidden = true;
            return;
          }

          section.replaceWith(replacement);
          initScope(replacement);
        })
        .catch(() => {
          section.hidden = true;
        })
        .finally(() => {
          delete section.dataset.productRecommendationsLoading;
        });
    });
  }

  function initRecentlyViewedProducts(scope = document) {
    collect(scope, '[data-recently-viewed-products]').forEach((section) => {
      if (section.dataset.recentlyViewedInitialized === 'true') return;
      section.dataset.recentlyViewedInitialized = 'true';

      const currentHandle = String(section.dataset.currentHandle || '').trim();
      const limit = Number(section.dataset.limit || 4);
      const cardSectionId = String(section.dataset.cardSectionId || 'product-card-fragment').trim();
      const grid = section.querySelector('[data-recently-viewed-products-grid]');
      const handles = getRecentlyViewedProductHandles(currentHandle, limit);

      if (!grid || handles.length === 0) {
        section.hidden = true;
        return;
      }

      Promise.all(handles.map(async (handle) => {
        try {
          const markup = await fetchSectionMarkup(buildStoreProductUrl(handle), cardSectionId);
          const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
          const productCard = parsedDocument.querySelector('[data-product-card]');

          return productCard ? productCard.outerHTML : '';
        } catch (error) {
          return '';
        }
      }))
        .then((cards) => {
          const renderedCards = cards.filter(Boolean);

          if (renderedCards.length === 0) {
            section.hidden = true;
            return;
          }

          grid.innerHTML = renderedCards.join('');
          section.hidden = false;
          initScope(grid);
          section.__ksHorizontalScrollUpdate?.();
        })
        .catch(() => {
          section.hidden = true;
        });
    });
  }

  function initProductSections(scope = document) {
    collect(scope, '[data-product-section-id]').forEach((section) => {
      if (section.dataset.productInitialized === 'true') return;

      const form = section.querySelector('.ks-product__form');
      const variantInput = section.querySelector('[data-variant-id-input]');
      const variantData = section.querySelector('[data-product-variants]');

      if (!form || !variantInput || !variantData) return;

      section.dataset.productInitialized = 'true';
      recordRecentlyViewedProduct(section.dataset.productHandle);

      const variants = JSON.parse(variantData.textContent);
      const optionInputs = Array.from(section.querySelectorAll('[data-option-input]'));
      const hasOptionInputs = optionInputs.length > 0;
      const addToCart = section.querySelector('[data-add-to-cart]');
      const addToCartText = section.querySelector('[data-add-to-cart-text]');
      const soldOutMessage = section.querySelector('[data-sold-out-message]');
      const backInStock = section.querySelector('[data-back-in-stock]');
      const backInStockTrigger = backInStock?.querySelector('[data-back-in-stock-trigger]');
      const backInStockPanel = backInStock?.querySelector('[data-back-in-stock-panel]');
      const backInStockFields = backInStock?.querySelector('[data-back-in-stock-fields]');
      const backInStockEmail = backInStock?.querySelector('[data-back-in-stock-email]');
      const backInStockSubmit = backInStock?.querySelector('[data-back-in-stock-submit]');
      const backInStockSubmitLabel = backInStock?.querySelector('[data-back-in-stock-submit-label]');
      const backInStockStatus = backInStock?.querySelector('[data-back-in-stock-status]');
      const stickyProductFormBar = section.parentElement?.querySelector(
        `[data-sticky-product-form-bar="${section.dataset.productSectionId}"]`
      );
      const stickyProductFormTrigger = stickyProductFormBar?.querySelector('[data-sticky-product-form-trigger]');
      const priceEl = section.querySelector('[data-product-price]');
      const comparePriceEl = section.querySelector('[data-product-compare-price]');
      const saleBadgeEl = section.querySelector('[data-product-sale-badge]');
      const skuRow = section.querySelector('[data-product-sku-row]');
      const skuEl = section.querySelector('[data-product-sku]');
      const inventoryStatus = section.querySelector('[data-product-inventory-status]');
      const inventoryText = section.querySelector('[data-product-inventory-text]');
      const mediaItems = Array.from(section.querySelectorAll('.ks-product-media__item[data-media-id]'));
      const mediaTriggers = Array.from(section.querySelectorAll('[data-media-trigger]'));
      const mediaPrev = section.querySelector('[data-media-prev]');
      const mediaNext = section.querySelector('[data-media-next]');
      const mediaSwipeSurface = section.querySelector('[data-media-swipe]');
      const mediaProgress = section.querySelector('[data-media-progress]');
      const mediaProgressIndicator = section.querySelector('[data-media-progress-indicator]');
      const mediaImages = Array.from(section.querySelectorAll('.ks-product-media__image'));
      const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
      const combinedRoot = section.querySelector('[data-combined-product-listings]');
      let activeVariant = null;
      let combinedSwapInProgress = false;
      let mediaScrollFrame = null;
      let mediaScrollEndTimer = null;
      let mediaScrollRestoreFrame = null;
      let mediaLoopEnabled = false;
      let mediaLoopJumping = false;
      let mediaLoopClones = [];
      let stickyProductFormScrollFrame = null;
      let updateStickyProductFormBarState = () => {};

      if (stickyProductFormBar && stickyProductFormTrigger) {
        window.__ksStickyProductFormBarCleanup?.();

        const stickyProductFormController = new AbortController();
        const footer = document.querySelector('.ks-footer');

        updateStickyProductFormBarState = () => {
          const sourceButton = addToCart || form;
          const sourceButtonRect = sourceButton.getBoundingClientRect();
          const footerRect = footer?.getBoundingClientRect();
          const sourceButtonHasPassed = sourceButtonRect.bottom <= 0;
          const footerHasReachedViewport = Boolean(footerRect && footerRect.top <= window.innerHeight);
          const shouldShow = Boolean(activeVariant?.available)
            && sourceButtonHasPassed
            && !footerHasReachedViewport;

          stickyProductFormBar.classList.toggle('is-visible', shouldShow);
          stickyProductFormBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
          stickyProductFormTrigger.tabIndex = shouldShow ? 0 : -1;
        };

        const queueStickyProductFormBarUpdate = () => {
          if (stickyProductFormScrollFrame) return;

          stickyProductFormScrollFrame = window.requestAnimationFrame(() => {
            updateStickyProductFormBarState();
            stickyProductFormScrollFrame = null;
          });
        };

        stickyProductFormTrigger.addEventListener('click', () => {
          const rootStyles = window.getComputedStyle(document.documentElement);
          const stickyHeaderOffset = Number.parseFloat(
            rootStyles.getPropertyValue('--theme-sticky-header-offset')
          ) || 0;
          const formScrollMargin = Number.parseFloat(window.getComputedStyle(form).scrollMarginTop) || 0;
          const formTop = form.getBoundingClientRect().top + window.scrollY;
          const targetScrollTop = Math.max(
            0,
            formTop - Math.max(formScrollMargin, stickyHeaderOffset + 32)
          );

          window.scrollTo({
            top: targetScrollTop,
            behavior: reducedMotionMedia.matches ? 'auto' : 'smooth',
          });
        }, { signal: stickyProductFormController.signal });

        window.addEventListener('scroll', queueStickyProductFormBarUpdate, {
          passive: true,
          signal: stickyProductFormController.signal,
        });
        window.addEventListener('resize', queueStickyProductFormBarUpdate, {
          passive: true,
          signal: stickyProductFormController.signal,
        });

        const cleanupStickyProductFormBar = () => {
          stickyProductFormController.abort();
          window.cancelAnimationFrame(stickyProductFormScrollFrame);
          stickyProductFormBar.classList.remove('is-visible');
          stickyProductFormBar.setAttribute('aria-hidden', 'true');
          stickyProductFormTrigger.tabIndex = -1;

          if (window.__ksStickyProductFormBarCleanup === cleanupStickyProductFormBar) {
            delete window.__ksStickyProductFormBarCleanup;
          }
        };

        window.__ksStickyProductFormBarCleanup = cleanupStickyProductFormBar;
        window.requestAnimationFrame(updateStickyProductFormBarState);
      } else if (!document.querySelector('[data-sticky-product-form-bar]')) {
        window.__ksStickyProductFormBarCleanup?.();
      }

      mediaImages.forEach((image) => {
        const markMediaLoaded = () => {
          image.classList.remove('is-loading');
          image.classList.add('is-loaded');
        };

        if (image.complete && image.naturalWidth > 0) {
          markMediaLoaded();
          return;
        }

        image.classList.add('is-loading');
        image.addEventListener('load', markMediaLoaded, { once: true });
        image.addEventListener('error', markMediaLoaded, { once: true });
      });

      function createMediaLoopClone(item, mediaIndex, position) {
        const clone = item.cloneNode(true);

        clone.hidden = false;
        clone.classList.remove('is-active');
        clone.dataset.mediaLoopClone = position;
        clone.dataset.mediaLoopIndex = String(mediaIndex);
        clone.removeAttribute('data-media-id');
        clone.removeAttribute('id');
        clone.removeAttribute('role');
        clone.removeAttribute('aria-labelledby');
        clone.setAttribute('aria-hidden', 'true');
        clone.style.pointerEvents = 'none';

        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('[data-product-media-zoom-open]').forEach((element) => {
          element.removeAttribute('data-product-media-zoom-open');
        });
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
          element.tabIndex = -1;
        });
        clone.querySelectorAll('img').forEach((image) => {
          image.removeAttribute('data-image-reveal');
          image.classList.remove('is-loading');
          image.classList.add('is-loaded');
        });

        return clone;
      }

      function enableMobileMediaLoop() {
        if (mediaLoopEnabled || !mediaSwipeSurface || mediaItems.length < 2) return;

        const leadingClone = createMediaLoopClone(
          mediaItems[mediaItems.length - 1],
          mediaItems.length - 1,
          'leading'
        );
        const trailingClone = createMediaLoopClone(mediaItems[0], 0, 'trailing');

        mediaSwipeSurface.insertBefore(leadingClone, mediaItems[0]);
        mediaSwipeSurface.append(trailingClone);
        mediaLoopClones = [leadingClone, trailingClone];
        mediaLoopEnabled = true;
      }

      function disableMobileMediaLoop() {
        mediaLoopClones.forEach((clone) => clone.remove());
        mediaLoopClones = [];
        mediaLoopEnabled = false;
      }

      function getMediaStageIndex(mediaIndex) {
        return mediaLoopEnabled ? mediaIndex + 1 : mediaIndex;
      }

      function jumpToMediaStageIndex(stageIndex) {
        if (!mediaSwipeSurface) return;

        window.cancelAnimationFrame(mediaScrollRestoreFrame);
        mediaLoopJumping = true;
        const previousScrollBehavior = mediaSwipeSurface.style.scrollBehavior;

        mediaSwipeSurface.style.scrollBehavior = 'auto';
        mediaSwipeSurface.scrollLeft = stageIndex * mediaSwipeSurface.clientWidth;
        mediaScrollRestoreFrame = window.requestAnimationFrame(() => {
          mediaSwipeSurface.style.scrollBehavior = previousScrollBehavior;
          mediaLoopJumping = false;
        });
      }

      function settleMobileMediaLoop() {
        if (!mobileMediaQuery.matches || !mediaLoopEnabled || !mediaSwipeSurface) return;

        const slideWidth = mediaSwipeSurface.clientWidth || 1;
        const stageIndex = Math.round(mediaSwipeSurface.scrollLeft / slideWidth);

        if (stageIndex <= 0) {
          updateMediaSelection(mediaItems.length - 1);
          updateMediaProgress(mediaItems.length - 1);
          jumpToMediaStageIndex(mediaItems.length);
        } else if (stageIndex >= mediaItems.length + 1) {
          updateMediaSelection(0);
          updateMediaProgress(0);
          jumpToMediaStageIndex(1);
        }
      }

      function updateMediaSelection(activeIndex) {
        mediaItems.forEach((item, index) => {
          const isActive = index === activeIndex;
          item.classList.toggle('is-active', isActive);
          item.hidden = mobileMediaQuery.matches ? false : !isActive;
        });

        mediaTriggers.forEach((trigger) => {
          const isActive = String(trigger.dataset.mediaTrigger) === String(
            mediaItems[activeIndex]?.dataset.mediaId
          );
          trigger.classList.toggle('is-active', isActive);
          trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
          trigger.tabIndex = isActive ? 0 : -1;
        });
      }

      function updateMediaProgress(position) {
        if (!mediaProgress || position < 0) return;

        mediaProgress.style.setProperty('--media-progress-index', String(position));
        mediaProgress.setAttribute('aria-valuenow', String(Math.round(position) + 1));
        mediaProgressIndicator?.style.removeProperty('transform');
      }

      function setActiveMedia(mediaId, options = {}) {
        if (!mediaId) return;

        const activeIndex = mediaItems.findIndex(
          (item) => String(item.dataset.mediaId) === String(mediaId)
        );

        if (activeIndex < 0) return;

        updateMediaSelection(activeIndex);
        updateMediaProgress(activeIndex);

        if (mobileMediaQuery.matches && options.scroll !== false && mediaSwipeSurface) {
          mediaSwipeSurface.scrollTo({
            left: getMediaStageIndex(activeIndex) * mediaSwipeSurface.clientWidth,
            behavior: reducedMotionMedia.matches ? 'auto' : 'smooth'
          });
        }
      }

      function getActiveMediaIndex() {
        return mediaItems.findIndex((item) => item.classList.contains('is-active'));
      }

      function stepMedia(direction) {
        if (mediaItems.length < 2) return;

        const currentIndex = getActiveMediaIndex();
        const safeIndex = currentIndex >= 0 ? currentIndex : 0;
        const nextIndex = (safeIndex + direction + mediaItems.length) % mediaItems.length;
        const nextItem = mediaItems[nextIndex];

        if (!nextItem) return;

        setActiveMedia(nextItem.dataset.mediaId);
      }

      if (mediaSwipeSurface && mediaItems.length > 1) {
        const syncMediaMode = () => {
          const activeIndex = Math.max(getActiveMediaIndex(), 0);

          if (mobileMediaQuery.matches) {
            enableMobileMediaLoop();
            updateMediaSelection(activeIndex);
            jumpToMediaStageIndex(getMediaStageIndex(activeIndex));
          } else {
            disableMobileMediaLoop();
            updateMediaSelection(activeIndex);
            mediaSwipeSurface.scrollLeft = 0;
          }
          updateMediaProgress(activeIndex);
        };

        mediaSwipeSurface.addEventListener('scroll', () => {
          if (!mobileMediaQuery.matches || mediaLoopJumping) return;

          window.clearTimeout(mediaScrollEndTimer);
          mediaScrollEndTimer = window.setTimeout(settleMobileMediaLoop, 100);

          if (mediaScrollFrame) return;

          mediaScrollFrame = requestAnimationFrame(() => {
            const slideWidth = mediaSwipeSurface.clientWidth || 1;
            const stagePosition = mediaSwipeSurface.scrollLeft / slideWidth;
            let position = stagePosition;

            if (mediaLoopEnabled) {
              if (stagePosition < 1) {
                position = (1 - stagePosition) * (mediaItems.length - 1);
              } else if (stagePosition > mediaItems.length) {
                position = (mediaItems.length + 1 - stagePosition) * (mediaItems.length - 1);
              } else {
                position = stagePosition - 1;
              }
            }

            position = Math.max(0, Math.min(mediaItems.length - 1, position));
            const activeIndex = Math.round(position);

            updateMediaSelection(activeIndex);
            updateMediaProgress(position);
            mediaScrollFrame = null;
          });
        }, { passive: true });

        if ('onscrollend' in window) {
          mediaSwipeSurface.addEventListener('scrollend', settleMobileMediaLoop);
        }

        mobileMediaQuery.addEventListener('change', syncMediaMode);

        if (typeof ResizeObserver === 'function') {
          const mediaResizeObserver = new ResizeObserver(syncMediaMode);
          mediaResizeObserver.observe(mediaSwipeSurface);
          section.__ksProductMediaResizeObserver = mediaResizeObserver;
        }

        requestAnimationFrame(() => {
          syncMediaMode();
        });
      }

      function getSelectedOptions() {
        const selected = [];
        const positions = Array.from(
          new Set(optionInputs.map((input) => Number(input.dataset.optionPosition)))
        ).sort((a, b) => a - b);

        positions.forEach((position) => {
          const inputsForPosition = optionInputs.filter(
            (input) => Number(input.dataset.optionPosition) === position
          );
          const selectedInput = inputsForPosition.find((input) => (
            input.type === 'radio'
              ? input.checked
              : String(input.value || '').trim() !== ''
          ));

          if (selectedInput) {
            selected.push({
              position,
              value: selectedInput.value,
            });
          }
        });

        return selected;
      }

      function updateSelectedLabels() {
        optionInputs.forEach((input) => {
          if (input.type === 'radio' && !input.checked) return;

          const label = section.querySelector(
            `[data-option-value-label="${input.dataset.optionPosition}"]`
          );

          if (label) {
            label.textContent = input.value;
          }
        });
      }

      function resetBackInStockPanel() {
        if (!backInStock) return;

        backInStock.classList.remove('is-success');
        if (backInStockPanel) backInStockPanel.hidden = true;
        if (backInStockFields) backInStockFields.hidden = false;
        if (backInStockEmail) backInStockEmail.disabled = true;
        if (backInStockTrigger) backInStockTrigger.setAttribute('aria-expanded', 'false');
        if (backInStockStatus) {
          backInStockStatus.textContent = '';
          backInStockStatus.removeAttribute('data-status');
        }
      }

      function updateBackInStockState(variant) {
        if (!backInStock) return;

        const shouldShow = Boolean(variant && !variant.available);
        const nextVariantId = shouldShow ? String(variant.id || '') : '';
        const variantChanged = backInStock.dataset.variantId !== nextVariantId;

        backInStock.hidden = !shouldShow;
        backInStock.dataset.variantId = nextVariantId;

        if (!shouldShow || variantChanged) {
          resetBackInStockPanel();
        }
      }

      function toggleBackInStockPanel() {
        if (!backInStockPanel || !backInStockTrigger || backInStock.hidden) return;

        const shouldOpen = backInStockPanel.hidden;
        backInStockPanel.hidden = !shouldOpen;
        if (backInStockEmail) backInStockEmail.disabled = !shouldOpen;
        backInStockTrigger.setAttribute('aria-expanded', String(shouldOpen));

        if (shouldOpen) {
          window.requestAnimationFrame(() => backInStockEmail?.focus());
        }
      }

      async function submitBackInStockRequest() {
        if (
          !backInStock
          || !backInStockEmail
          || !backInStockSubmit
          || !backInStockSubmitLabel
          || !backInStockStatus
        ) return;

        if (!backInStockEmail.checkValidity()) {
          backInStockEmail.reportValidity();
          return;
        }

        const publicKey = String(document.body.dataset.klaviyoPublicApiKey || '').trim();
        const variantId = String(backInStock.dataset.variantId || '').trim();
        const email = String(backInStockEmail.value || '').trim();
        const defaultLabel = String(backInStockSubmit.dataset.defaultLabel || 'Notify me');
        const loadingLabel = String(backInStockSubmit.dataset.loadingLabel || 'Submitting...');
        const successMessage = String(
          backInStock.dataset.successMessage
          || "You're on the list. We'll let you know when this item is back in stock."
        );
        const errorMessage = String(
          backInStock.dataset.errorMessage
          || "We couldn't save your request. Please try again."
        );

        backInStockStatus.textContent = '';
        backInStockStatus.removeAttribute('data-status');
        backInStockSubmit.disabled = true;
        backInStockSubmit.setAttribute('aria-busy', 'true');
        backInStockSubmitLabel.textContent = loadingLabel;

        try {
          if (!publicKey || !variantId) {
            throw new Error('Back in stock notifications are not configured.');
          }

          const controller = typeof AbortController === 'function' ? new AbortController() : null;
          const timeout = window.setTimeout(() => controller?.abort(), 8000);
          let response;

          try {
            response = await fetch(
              'https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id='
                + encodeURIComponent(publicKey),
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  revision: '2026-07-15',
                },
                body: JSON.stringify({
                  data: {
                    type: 'back-in-stock-subscription',
                    attributes: {
                      profile: {
                        data: {
                          type: 'profile',
                          attributes: { email },
                        },
                      },
                      channels: ['EMAIL'],
                    },
                    relationships: {
                      variant: {
                        data: {
                          type: 'catalog-variant',
                          id: `$shopify:::$default:::${variantId}`,
                        },
                      },
                    },
                  },
                }),
                signal: controller?.signal,
              }
            );
          } finally {
            window.clearTimeout(timeout);
          }

          if (!response.ok) {
            throw new Error(`Klaviyo back in stock request failed with ${response.status}.`);
          }

          backInStock.classList.add('is-success');
          if (backInStockFields) backInStockFields.hidden = true;
          backInStockStatus.dataset.status = 'success';
          backInStockStatus.textContent = successMessage;
          announce(successMessage);
        } catch (error) {
          console.warn(error);
          backInStockStatus.dataset.status = 'error';
          backInStockStatus.textContent = errorMessage;
          announce(errorMessage);
        } finally {
          backInStockSubmit.disabled = false;
          backInStockSubmit.removeAttribute('aria-busy');
          backInStockSubmitLabel.textContent = defaultLabel;
        }
      }

      function updateOptionAvailability() {
        if (!hasOptionInputs) return;

        let selectionChanged = false;

        optionInputs.forEach((input) => {
          const currentPosition = Number(input.dataset.optionPosition);
          const currentValue = String(input.value || '');

          if (!currentPosition || currentValue === '') return;

          const otherSelections = getSelectedOptions().filter(
            (selection) => selection.position !== currentPosition
          );

          const matchingVariants = variants.filter((variant) => {
            if (variant.options[currentPosition - 1] !== currentValue) return false;

            return otherSelections.every((selection) => (
              variant.options[selection.position - 1] === selection.value
            ));
          });
          const hasVariantMatch = matchingVariants.length > 0;
          const hasAvailableMatch = matchingVariants.some((variant) => variant.available);

          input.disabled = !hasVariantMatch;

          const label = input.nextElementSibling;
          if (label) {
            label.classList.toggle('is-unavailable', !hasAvailableMatch);
            label.setAttribute('aria-disabled', String(!hasVariantMatch));

            if (!label.dataset.optionTitle) {
              label.dataset.optionTitle = label.getAttribute('title') || currentValue;
            }

            label.setAttribute(
              'title',
              hasAvailableMatch
                ? label.dataset.optionTitle
                : `${label.dataset.optionTitle} - Sold out`
            );
          }

          if (input.checked && !hasVariantMatch) {
            input.checked = false;
            selectionChanged = true;
          }
        });

        if (selectionChanged) {
          updateSelectedLabels();
          updateVariantState();
        }
      }

      function resetQuickAddState() {
        if (section.dataset.requireCompleteSelection !== 'true') return;

        optionInputs.forEach((input) => {
          if (input.type === 'radio') {
            input.checked = false;
          } else {
            input.value = '';
          }
        });

        variantInput.value = '';
        section.dataset.quickAddReady = 'false';

        if (addToCart) {
          addToCart.disabled = true;
          addToCart.hidden = false;
        }
        if (soldOutMessage) soldOutMessage.hidden = true;
        if (addToCartText && addToCart) {
          addToCartText.textContent = addToCart.dataset.labelAvailable;
        }
        activeVariant = null;
        updateBackInStockState(null);
        updateStickyProductFormBarState();

        updateOptionAvailability();
      }

      function updateVariantState() {
        const selectedOptions = getSelectedOptions();
        const requiresCompleteSelection = section.dataset.requireCompleteSelection === 'true';
        const requiredOptionsCount = Number(section.dataset.requiredOptionsCount || 0);
        const hasCompleteSelection = !requiresCompleteSelection || selectedOptions.length >= requiredOptionsCount;
        const matchedVariant = hasOptionInputs
          ? (hasCompleteSelection
            ? variants.find((variant) => selectedOptions.every((selection) => (
            variant.options[selection.position - 1] === selection.value
          )))
            : null)
          : variants.find((variant) => String(variant.id) === String(variantInput.value)) || variants[0];

        section.dataset.quickAddReady = hasCompleteSelection && matchedVariant ? 'true' : 'false';

        if (!hasCompleteSelection) {
          variantInput.value = '';

          if (addToCart) {
            addToCart.disabled = true;
            addToCart.hidden = false;
          }
          if (soldOutMessage) soldOutMessage.hidden = true;
          if (addToCartText && addToCart) {
            addToCartText.textContent = addToCart.dataset.labelAvailable;
          }
          activeVariant = null;
          updateBackInStockState(null);
          updateStickyProductFormBarState();
          section.dispatchEvent(new CustomEvent('ks:variant-change', {
            detail: { variant: null },
          }));
          return;
        }

        if (!matchedVariant) {
          section.dataset.quickAddReady = 'false';

          if (addToCart) {
            addToCart.disabled = true;
            addToCart.hidden = false;
          }
          if (soldOutMessage) soldOutMessage.hidden = true;
          if (addToCartText && addToCart) {
            addToCartText.textContent = addToCart.dataset.labelUnavailable;
          }
          activeVariant = null;
          updateBackInStockState(null);
          updateStickyProductFormBarState();
          section.dispatchEvent(new CustomEvent('ks:variant-change', {
            detail: { variant: null },
          }));
          return;
        }

        variantInput.value = matchedVariant.id;
        activeVariant = matchedVariant;

        if (priceEl) priceEl.textContent = matchedVariant.price;

        if (comparePriceEl) {
          comparePriceEl.textContent = matchedVariant.compareAtPrice || '';
          comparePriceEl.classList.toggle(
            'is-hidden',
            !(matchedVariant.compareAtPriceRaw > matchedVariant.priceRaw)
          );
        }

        if (saleBadgeEl) {
          saleBadgeEl.textContent = matchedVariant.saleText || '';
          saleBadgeEl.classList.toggle('is-hidden', !matchedVariant.saleText);
        }

        if (skuEl) skuEl.textContent = matchedVariant.sku || '';
        if (skuRow) skuRow.classList.toggle('is-hidden', !matchedVariant.sku);

        if (inventoryStatus && inventoryText) {
          const inventoryThreshold = Number(inventoryStatus.dataset.inventoryThreshold || 5);
          const inventoryQuantity = Number(matchedVariant.inventoryQuantity || 0);
          const tracksInventory = Boolean(matchedVariant.inventoryManagement);
          const allowsBackorder = matchedVariant.inventoryPolicy === 'continue';
          const isLowInventory = matchedVariant.available
            && tracksInventory
            && !allowsBackorder
            && inventoryQuantity <= inventoryThreshold;
          const inventoryLabel = isLowInventory
            ? String(inventoryStatus.dataset.lowStockLabel || 'Only [quantity] left - order soon.')
              .replace('[quantity]', String(inventoryQuantity))
            : String(inventoryStatus.dataset.inStockLabel || 'In stock');

          inventoryStatus.classList.toggle('is-hidden', !matchedVariant.available);
          inventoryStatus.classList.toggle('is-low', isLowInventory);
          inventoryText.textContent = inventoryLabel;
        }

        if (addToCart) {
          addToCart.disabled = !matchedVariant.available;
          addToCart.hidden = Boolean(soldOutMessage && !matchedVariant.available);
        }
        if (soldOutMessage) soldOutMessage.hidden = matchedVariant.available;
        if (addToCartText && addToCart) {
          addToCartText.textContent = matchedVariant.available
            ? addToCart.dataset.labelAvailable
            : addToCart.dataset.labelSoldout;
        }
        updateBackInStockState(matchedVariant);
        updateStickyProductFormBarState();

        setActiveMedia(matchedVariant.featuredMediaId);
        section.dispatchEvent(new CustomEvent('ks:variant-change', {
          detail: { variant: matchedVariant },
        }));
      }

      function redirectSoldOutQuickAddVariant(variant) {
        if (
          section.dataset.redirectSoldOutToProduct !== 'true'
          || !variant
          || variant.available
        ) return false;

        const productUrl = String(section.dataset.productUrl || '').trim();

        if (!productUrl) return false;

        const targetUrl = new URL(productUrl, window.location.origin);
        targetUrl.searchParams.set('variant', String(variant.id));
        window.location.assign(targetUrl.href);
        return true;
      }

      function bindCombinedProductTriggers() {
        if (!combinedRoot) return;

        collect(combinedRoot, '[data-combined-product-trigger]').forEach((trigger) => {
          if (trigger.dataset.combinedInitialized === 'true') return;
          trigger.dataset.combinedInitialized = 'true';

          function prefetchCombinedProduct() {
            const targetUrl = trigger.dataset.productUrl;

            if (!targetUrl || trigger.disabled) return;

            fetchDocumentMarkup(targetUrl).catch(() => {});
          }

          trigger.addEventListener('pointerenter', prefetchCombinedProduct, { passive: true });
          trigger.addEventListener('focus', prefetchCombinedProduct);

          trigger.addEventListener('click', async (event) => {
            const targetUrl = trigger.dataset.productUrl;
            const targetTitle = trigger.dataset.productTitle || 'Product';
            const usesCoarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
            const shouldMoveFocus = event.detail === 0 && !usesCoarsePointer;

            if (!targetUrl || combinedSwapInProgress || trigger.disabled) return;

            combinedSwapInProgress = true;
            section.dataset.loading = 'true';
            section.setAttribute('aria-busy', 'true');
            combinedRoot.dataset.loading = 'true';
            announce(`Loading ${targetTitle}.`);

            try {
              await replaceProductSection(section, targetUrl, {
                announceLabel: targetTitle,
                moveFocus: shouldMoveFocus,
              });

              const currentHistoryState = window.history.state && typeof window.history.state === 'object'
                ? window.history.state
                : {};

              if (currentHistoryState.ksCombinedProduct !== true) {
                window.history.replaceState({ ...currentHistoryState, ksCombinedProduct: true }, '', window.location.href);
              }

              window.history.pushState({ ...currentHistoryState, ksCombinedProduct: true }, '', targetUrl);
            } catch (error) {
              window.location.assign(targetUrl);
            } finally {
              combinedSwapInProgress = false;
            }
          });
        });
      }

      backInStockTrigger?.addEventListener('click', toggleBackInStockPanel);
      backInStockSubmit?.addEventListener('click', submitBackInStockRequest);
      backInStockEmail?.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        submitBackInStockRequest();
      });

      optionInputs.forEach((input) => {
        input.addEventListener('change', () => {
          updateSelectedLabels();
          updateVariantState();
          updateOptionAvailability();

          const selectedVariant = variants.find((variant) => (
            String(variant.id) === String(variantInput.value)
          ));

          if (redirectSoldOutQuickAddVariant(selectedVariant)) return;

          if (
            section.dataset.autoSubmitVariant === 'true'
            && section.dataset.quickAddReady === 'true'
            && section.dataset.variantSubmitting !== 'true'
          ) {
            if (selectedVariant?.available) {
              section.dataset.variantSubmitting = 'true';
              section.dataset.loading = 'true';
              announce('Adding selected variant to cart.');
              form.requestSubmit();
            }
          }
        });
      });

      section.addEventListener('ks:quick-add-reset', resetQuickAddState);

      mediaTriggers.forEach((trigger) => {
        trigger.addEventListener('click', () => {
          setActiveMedia(trigger.dataset.mediaTrigger);
        });

        trigger.addEventListener('keydown', (event) => {
          if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
            return;
          }

          event.preventDefault();

          if (mediaTriggers.length === 0) return;

          const currentIndex = mediaTriggers.indexOf(trigger);
          let nextIndex = currentIndex;

          if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = mediaTriggers.length - 1;
          } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            nextIndex = (currentIndex - 1 + mediaTriggers.length) % mediaTriggers.length;
          } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            nextIndex = (currentIndex + 1) % mediaTriggers.length;
          }

          const nextTrigger = mediaTriggers[nextIndex];

          if (!nextTrigger) return;

          setActiveMedia(nextTrigger.dataset.mediaTrigger);
          nextTrigger.focus();
        });
      });

      if (mediaPrev) {
        mediaPrev.addEventListener('click', () => {
          stepMedia(-1);
        });
      }

      if (mediaNext) {
        mediaNext.addEventListener('click', () => {
          stepMedia(1);
        });
      }

      updateSelectedLabels();
      updateVariantState();
      updateOptionAvailability();
      bindCombinedProductTriggers();
    });
  }

  function initCompleteSets(scope = document) {
    collect(scope, '[data-complete-set]').forEach((completeSet) => {
      if (completeSet.dataset.completeSetInitialized === 'true') return;
      completeSet.dataset.completeSetInitialized = 'true';

      const section = completeSet.closest('[data-product-section-id]');
      const form = completeSet.closest('.ks-product__form');
      const currentVariantInput = form?.querySelector('[data-variant-id-input]');
      const variantData = section?.querySelector('[data-product-variants]');
      const companionSelect = completeSet.querySelector('[data-complete-set-companion-variant]');
      const companionImage = completeSet.querySelector(
        '.ks-complete-set__product:last-child .ks-complete-set__image'
      );
      const currentSelection = completeSet.querySelector('[data-complete-set-current-selection]');
      const button = completeSet.querySelector('[data-complete-set-add]');
      const buttonLabel = completeSet.querySelector('[data-complete-set-button-label]');
      const status = completeSet.querySelector('[data-complete-set-status]');
      const currentSizePositionValue = completeSet.dataset.currentSizePosition;
      const currentSizePosition = currentSizePositionValue === ''
        ? -1
        : Number(currentSizePositionValue);

      if (!section || !form || !currentVariantInput || !variantData || !companionSelect || !button) return;

      let currentVariants = [];

      try {
        currentVariants = JSON.parse(variantData.textContent);
      } catch (error) {
        currentVariants = [];
      }

      const getCurrentVariant = () => currentVariants.find((variant) => (
        String(variant.id) === String(currentVariantInput.value)
      ));
      const getCompanionOption = () => companionSelect.selectedOptions[0] || null;

      const updateCompanionImage = () => {
        const option = getCompanionOption();

        if (!companionImage || !option?.dataset.imageSrc) return;

        companionImage.src = option.dataset.imageSrc;
        companionImage.removeAttribute('srcset');
        companionImage.alt = option.dataset.imageAlt || completeSet.dataset.companionProductTitle || '';
      };

      const updateState = (variant = getCurrentVariant()) => {
        const companionOption = getCompanionOption();
        const canAddCurrent = Boolean(variant?.available);
        const canAddCompanion = Boolean(companionOption && !companionOption.disabled && companionOption.value);

        button.disabled = !(canAddCurrent && canAddCompanion);

        if (currentSelection && variant?.options) {
          currentSelection.textContent = Number.isInteger(currentSizePosition)
            && currentSizePosition >= 0
            && variant.options[currentSizePosition]
            ? variant.options[currentSizePosition]
            : variant.options.join(' / ');
        }

        updateCompanionImage();
      };

      companionSelect.addEventListener('change', () => {
        if (status) status.textContent = '';
        updateState();
      });

      section.addEventListener('ks:variant-change', (event) => {
        if (status) status.textContent = '';
        updateState(event.detail?.variant || null);
      });

      button.addEventListener('click', async () => {
        const currentVariant = getCurrentVariant();
        const companionOption = getCompanionOption();
        const companionVariantId = Number(companionOption?.value || 0);

        if (!currentVariant?.available || !companionVariantId || companionOption.disabled) {
          if (status) status.textContent = 'Choose available options for both pieces.';
          return;
        }

        const defaultLabel = button.dataset.defaultLabel || 'Add set to cart';
        const loadingLabel = button.dataset.loadingLabel || 'Adding set…';
        const loadingDrawer = openCartDrawerLoading(form);

        button.disabled = true;
        if (buttonLabel) buttonLabel.textContent = loadingLabel;
        if (status) status.textContent = '';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: [
                {
                  id: Number(currentVariant.id), quantity: 1,
                  ...(completeSet.dataset.currentFinalSale === 'true' ? { properties: { 'Final sale': 'No returns or exchanges' } } : {}),
                },
                {
                  id: companionVariantId, quantity: 1,
                  ...(completeSet.dataset.companionFinalSale === 'true' ? { properties: { 'Final sale': 'No returns or exchanges' } } : {}),
                },
              ],
            }),
          });
          const payload = await response.json();

          if (!response.ok) {
            throw new Error(payload.description || 'Unable to add the set to cart.');
          }

          const cart = await fetchCart();
          updateCartCount(cart.item_count);
          await refreshHeaderCartState({ openDrawer: true });
          announce('Both set pieces added to cart.');
          if (status) status.textContent = 'Both pieces were added to your cart.';
        } catch (error) {
          if (loadingDrawer) delete loadingDrawer.dataset.cartLoading;
          const message = error.message || 'Unable to add the set to cart.';
          announce(message);
          if (status) status.textContent = message;
        } finally {
          if (buttonLabel) buttonLabel.textContent = defaultLabel;
          updateState();
        }
      });

      updateState();
    });
  }

  function initCartDrawerScrollbars(scope = document) {
    collect(scope, '[data-cart-drawer-scroll]').forEach((container) => {
      const viewport = container.querySelector('[data-cart-drawer-scroll-viewport]');
      const indicator = container.querySelector('[data-cart-drawer-scrollbar-indicator]');

      if (!viewport || !indicator) return;

      const update = () => {
        const viewportHeight = viewport.clientHeight;
        const contentHeight = viewport.scrollHeight;
        const maxScroll = Math.max(0, contentHeight - viewportHeight);
        const isScrollable = viewportHeight > 0 && maxScroll > 1;

        container.classList.toggle('is-scrollable', isScrollable);

        if (!isScrollable) {
          indicator.style.height = '';
          indicator.style.transform = 'translate3d(0, 0, 0)';
          return;
        }

        const indicatorHeight = Math.max(24, viewportHeight * (viewportHeight / contentHeight));
        const availableTrack = Math.max(0, viewportHeight - indicatorHeight);
        const indicatorOffset = maxScroll > 0
          ? availableTrack * (viewport.scrollTop / maxScroll)
          : 0;

        indicator.style.height = `${indicatorHeight}px`;
        indicator.style.transform = `translate3d(0, ${indicatorOffset}px, 0)`;
      };

      container.__updateCartDrawerScrollbar = update;

      if (container.dataset.cartDrawerScrollbarBound !== 'true') {
        container.dataset.cartDrawerScrollbarBound = 'true';
        viewport.addEventListener('scroll', update, { passive: true });

        if (typeof ResizeObserver === 'function') {
          const resizeObserver = new ResizeObserver(update);
          resizeObserver.observe(viewport);
          resizeObserver.observe(viewport.firstElementChild || viewport);
          container.__cartDrawerResizeObserver = resizeObserver;
        }

        if (typeof MutationObserver === 'function') {
          const mutationObserver = new MutationObserver(update);
          mutationObserver.observe(viewport, { childList: true, subtree: true });
          container.__cartDrawerMutationObserver = mutationObserver;
        }
      }

      update();
    });
  }

  const parallaxMediaNodes = new Set();
  const parallaxMotionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
  let parallaxMediaFrame = 0;
  let parallaxMediaListenersInitialized = false;

  function updateParallaxMedia() {
    parallaxMediaFrame = 0;
    const reduceMotion = parallaxMotionPreference.matches;
    const viewportHeight = Math.max(window.innerHeight, 1);

    parallaxMediaNodes.forEach((media) => {
      if (!media.isConnected || !media.hasAttribute('data-ks-parallax')) {
        media.style.removeProperty('--ks-parallax-offset-y');
        parallaxMediaNodes.delete(media);
        return;
      }

      if (reduceMotion) {
        media.style.removeProperty('--ks-parallax-offset-y');
        return;
      }

      const rect = media.getBoundingClientRect();
      const speed = Math.min(100, Math.max(10, Number.parseFloat(media.dataset.ksParallaxSpeed) || 35));
      const viewportStrength = window.innerWidth < 768 ? 0.45 : 1;
      const maxTravel = 140 * (speed / 100) * viewportStrength;
      const elementCenter = rect.top + (rect.height / 2);
      const travelRange = Math.max((viewportHeight + rect.height) / 2, 1);
      const progress = Math.min(1, Math.max(-1, ((viewportHeight / 2) - elementCenter) / travelRange));
      const direction = media.dataset.ksParallaxDirection === 'down' ? 1 : -1;
      const offset = Math.round(progress * maxTravel * direction * 100) / 100;
      const nextOffset = `${offset}px`;

      if (media.style.getPropertyValue('--ks-parallax-offset-y') !== nextOffset) {
        media.style.setProperty('--ks-parallax-offset-y', nextOffset);
      }
    });
  }

  function scheduleParallaxMediaUpdate() {
    if (parallaxMediaFrame) return;
    parallaxMediaFrame = window.requestAnimationFrame(updateParallaxMedia);
  }

  function initParallaxMedia(scope = document) {
    collect(scope, '[data-ks-parallax]').forEach((media) => {
      parallaxMediaNodes.add(media);

      if (media.dataset.ksParallaxInitialized === 'true') return;
      media.dataset.ksParallaxInitialized = 'true';

      media.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', scheduleParallaxMediaUpdate, { once: true });
      });
      media.querySelectorAll('video').forEach((video) => {
        if (video.readyState < 1) video.addEventListener('loadedmetadata', scheduleParallaxMediaUpdate, { once: true });
      });
    });

    if (!parallaxMediaListenersInitialized) {
      parallaxMediaListenersInitialized = true;
      window.addEventListener('scroll', scheduleParallaxMediaUpdate, { passive: true });
      window.addEventListener('resize', scheduleParallaxMediaUpdate, { passive: true });

      if (typeof parallaxMotionPreference.addEventListener === 'function') {
        parallaxMotionPreference.addEventListener('change', scheduleParallaxMediaUpdate);
      } else {
        parallaxMotionPreference.addListener(scheduleParallaxMediaUpdate);
      }
    }

    scheduleParallaxMediaUpdate();
  }

  function cleanupParallaxMedia(scope) {
    collect(scope, '[data-ks-parallax]').forEach((media) => {
      media.style.removeProperty('--ks-parallax-offset-y');
      parallaxMediaNodes.delete(media);
    });
  }

  function registerMediaContainmentCleanup(group, cleanup) {
    if (!(group instanceof Element) || typeof cleanup !== 'function') return;

    group.__ksMediaContainmentCleanups = group.__ksMediaContainmentCleanups || [];
    group.__ksMediaContainmentCleanups.push(cleanup);
  }

  function cleanupMediaContainment(scope) {
    collect(scope, '[data-floating-media-containment-initialized], [data-offset-image-containment-initialized]').forEach((group) => {
      (group.__ksMediaContainmentCleanups || []).forEach((cleanup) => cleanup());
      delete group.__ksMediaContainmentCleanups;
    });
  }

  function initFloatingMediaGroupContainment(scope = document) {
    collect(scope, '.group:has(.basic-content-floating-image), .basic-content-group:has(.basic-content-floating-image)').forEach((group) => {
      if (group.dataset.floatingMediaContainmentInitialized === 'true') return;
      group.dataset.floatingMediaContainmentInitialized = 'true';

      const content = group.querySelector(':scope > .group__content, :scope > .basic-content-group__content');
      if (!content) return;

      const controller = new AbortController();
      const observedMedia = new WeakSet();
      const resizeObserver = typeof ResizeObserver === 'function'
        ? new ResizeObserver(() => schedule())
        : null;
      let updateFrame = null;

      const getFloatingMedia = () => [...group.querySelectorAll('.basic-content-floating-image')].filter((media) => {
        if (media.closest('.group, .basic-content-group') !== group) return false;

        return media.parentElement === content || media.parentElement?.parentElement === content;
      });

      const getMotionTravel = (media) => {
        const styles = window.getComputedStyle(media);
        if (styles.animationName === 'none') return 0;

        const drift = Number.parseFloat(styles.getPropertyValue('--floating-image-drift-y')) || 0;
        return Math.abs(drift) + 8;
      };

      const observeMedia = (media) => {
        if (observedMedia.has(media)) return;
        observedMedia.add(media);
        resizeObserver?.observe(media);

        media.querySelectorAll('img, video').forEach((asset) => {
          asset.addEventListener('load', schedule, { signal: controller.signal });
          asset.addEventListener('loadedmetadata', schedule, { signal: controller.signal });
        });
      };

      const update = () => {
        updateFrame = null;
        const floatingMedia = getFloatingMedia();

        if (floatingMedia.length === 0) {
          group.style.removeProperty('--floating-media-containment-height');
          return;
        }

        let containmentCoreHeight = 0;
        let topInset = 0;
        let bottomInset = 0;

        floatingMedia.forEach((media) => {
          observeMedia(media);
          const mediaHeight = media.offsetHeight;
          const mediaWidth = media.offsetWidth;
          const motionTravel = getMotionTravel(media);
          const styles = window.getComputedStyle(media);
          const offsetY = Number.parseFloat(styles.getPropertyValue('--floating-image-offset-y')) || 0;
          const rotation = Number.parseFloat(styles.getPropertyValue('--floating-image-rotation')) || 0;
          const verticalAlignment = styles.getPropertyValue('--floating-image-vertical-alignment').trim();
          const rotationRadians = rotation * (Math.PI / 180);
          const rotatedHeight = (
            Math.abs(mediaHeight * Math.cos(rotationRadians))
            + Math.abs(mediaWidth * Math.sin(rotationRadians))
          );

          let offsetSpace = 0;
          if (verticalAlignment === 'center') {
            offsetSpace = Math.abs(offsetY) * 2;
          } else if (verticalAlignment === 'top') {
            offsetSpace = Math.max(offsetY, 0);
            topInset = Math.max(topInset, Math.max(-offsetY, 0));
          } else if (verticalAlignment === 'bottom') {
            offsetSpace = Math.max(-offsetY, 0);
            bottomInset = Math.max(bottomInset, Math.max(offsetY, 0));
          }

          const requiredHeight = Math.max(mediaHeight, rotatedHeight) + (motionTravel * 2) + offsetSpace;
          containmentCoreHeight = Math.max(containmentCoreHeight, requiredHeight);
        });

        const containmentHeight = containmentCoreHeight + topInset + bottomInset;
        const nextHeight = `${Math.ceil(containmentHeight)}px`;
        if (group.style.getPropertyValue('--floating-media-containment-height') !== nextHeight) {
          group.style.setProperty('--floating-media-containment-height', nextHeight);
        }

        floatingMedia.forEach((media) => {
          const verticalAlignment = window.getComputedStyle(media)
            .getPropertyValue('--floating-image-vertical-alignment')
            .trim();
          let anchorShift = 0;

          if (verticalAlignment === 'top') {
            anchorShift = topInset;
          } else if (verticalAlignment === 'bottom') {
            anchorShift = -bottomInset;
          } else {
            anchorShift = (topInset - bottomInset) / 2;
          }

          const nextShift = `${Math.round(anchorShift * 100) / 100}px`;
          if (media.style.getPropertyValue('--floating-media-anchor-shift-y') !== nextShift) {
            media.style.setProperty('--floating-media-anchor-shift-y', nextShift);
          }
        });
      };

      function schedule() {
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(update);
      }

      const mutationObserver = typeof MutationObserver === 'function'
        ? new MutationObserver(schedule)
        : null;

      mutationObserver?.observe(group, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true,
      });

      window.addEventListener('resize', schedule, { passive: true, signal: controller.signal });

      registerMediaContainmentCleanup(group, () => {
        controller.abort();
        mutationObserver?.disconnect();
        resizeObserver?.disconnect();
        window.cancelAnimationFrame(updateFrame);
        group.style.removeProperty('--floating-media-containment-height');
        getFloatingMedia().forEach((media) => {
          media.style.removeProperty('--floating-media-anchor-shift-y');
        });
        delete group.dataset.floatingMediaContainmentInitialized;
      });

      schedule();
    });
  }

  function initOffsetImageGroupContainment(scope = document) {
    collect(scope, '.group:has(.ks-content-image, .basic-content__block--image), .basic-content-group:has(.ks-content-image, .basic-content__block--image)').forEach((group) => {
      if (group.dataset.offsetImageContainmentInitialized === 'true') return;
      group.dataset.offsetImageContainmentInitialized = 'true';

      const controller = new AbortController();
      const content = group.querySelector(':scope > .group__content, :scope > .basic-content-group__content');
      let updateFrame = null;

      if (!content) return;

      const getImages = () => [...content.querySelectorAll('.ks-content-image, .basic-content__block--image')].filter((image) => (
        image.closest('.group, .basic-content-group') === group
      ));

      const update = () => {
        const suffix = window.matchMedia('(min-width: 768px)').matches ? 'desktop' : 'mobile';
        const containment = { top: 0, right: 0, bottom: 0, left: 0 };

        getImages().forEach((image) => {
          const styles = window.getComputedStyle(image);
          const prefix = image.classList.contains('ks-content-image')
            ? '--content-image-offset-'
            : '--basic-image-offset-';

          Object.keys(containment).forEach((edge) => {
            const value = Number.parseFloat(styles.getPropertyValue(`${prefix}${edge}-${suffix}`)) || 0;
            containment[edge] = Math.max(containment[edge], value);
          });
        });

        Object.entries(containment).forEach(([edge, value]) => {
          const property = `--offset-image-containment-${edge}`;
          const nextValue = `${value}px`;
          if (group.style.getPropertyValue(property) !== nextValue) {
            group.style.setProperty(property, nextValue);
          }
        });
      };

      const schedule = () => {
        window.cancelAnimationFrame(updateFrame);
        updateFrame = window.requestAnimationFrame(update);
      };

      const mutationObserver = typeof MutationObserver === 'function'
        ? new MutationObserver(schedule)
        : null;

      mutationObserver?.observe(group, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        childList: true,
        subtree: true,
      });

      window.addEventListener('resize', schedule, { passive: true, signal: controller.signal });

      registerMediaContainmentCleanup(group, () => {
        controller.abort();
        mutationObserver?.disconnect();
        window.cancelAnimationFrame(updateFrame);
        ['top', 'right', 'bottom', 'left'].forEach((edge) => {
          group.style.removeProperty(`--offset-image-containment-${edge}`);
        });
        delete group.dataset.offsetImageContainmentInitialized;
      });

      schedule();
    });
  }

  function normalizeStickyCtaAnchorId(value) {
    return String(value || '')
      .trim()
      .replace(/^#/, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function resolveStickyCtaExcludedSection(anchorId) {
    const rawAnchorId = String(anchorId || '').trim().replace(/^#/, '');
    const normalizedAnchorId = normalizeStickyCtaAnchorId(rawAnchorId);
    const anchor = document.getElementById(rawAnchorId)
      || document.getElementById(normalizedAnchorId);

    if (!anchor) return null;

    return anchor.closest('.shopify-section') || anchor.nextElementSibling || anchor;
  }

  function stickyCtaElementIsVisible(element) {
    if (!element || !element.isConnected) return false;

    const styles = window.getComputedStyle(element);

    if (styles.display === 'none' || styles.visibility === 'hidden') return false;

    const rect = element.getBoundingClientRect();

    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function cleanupStickyPageCtas(scope = document) {
    collect(scope, '[data-sticky-page-cta]').forEach((stickyCta) => {
      stickyCta.__ksStickyPageCtaCleanup?.();
    });
  }

  function initStickyPageCtas(scope = document) {
    collect(scope, '[data-sticky-page-cta]').forEach((stickyCta) => {
      if (stickyCta.dataset.stickyPageCtaInitialized === 'true') return;

      stickyCta.dataset.stickyPageCtaInitialized = 'true';

      const controller = new AbortController();
      const ctaLink = stickyCta.querySelector('[data-sticky-page-cta-link]');
      const showAfterScroll = Number(stickyCta.dataset.showAfterScroll || 0);
      const deviceVisibility = stickyCta.dataset.deviceVisibility || 'all';
      const excludedAnchorIds = String(stickyCta.dataset.hideAnchorIds || '')
        .split(/[\n,]+/)
        .map((anchorId) => anchorId.trim())
        .filter(Boolean);
      const excludedSections = excludedAnchorIds
        .map(resolveStickyCtaExcludedSection)
        .filter(Boolean);
      let updateFrame = null;

      const setVisible = (shouldShow) => {
        stickyCta.classList.toggle('is-visible', shouldShow);
        stickyCta.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

        if (ctaLink?.matches('a, button')) {
          ctaLink.tabIndex = shouldShow ? 0 : -1;
        }
      };

      const update = () => {
        if (!stickyCta.isConnected) {
          stickyCta.__ksStickyPageCtaCleanup?.();
          return;
        }

        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const deviceAllowsCta = deviceVisibility === 'all'
          || (deviceVisibility === 'mobile' && isMobile)
          || (deviceVisibility === 'desktop' && !isMobile);
        const footerIsVisible = stickyCtaElementIsVisible(document.querySelector('.ks-footer'));
        const excludedSectionIsVisible = excludedSections.some(stickyCtaElementIsVisible);
        const productStickyBarIsVisible = Boolean(
          document.querySelector('[data-sticky-product-form-bar].is-visible')
        );
        const shouldShow = deviceAllowsCta
          && window.scrollY >= showAfterScroll
          && !footerIsVisible
          && !excludedSectionIsVisible
          && !productStickyBarIsVisible;

        setVisible(shouldShow);
      };

      const queueUpdate = () => {
        if (updateFrame) return;

        updateFrame = window.requestAnimationFrame(() => {
          updateFrame = null;
          update();
        });
      };

      window.addEventListener('scroll', queueUpdate, {
        passive: true,
        signal: controller.signal,
      });
      window.addEventListener('resize', queueUpdate, {
        passive: true,
        signal: controller.signal,
      });
      window.addEventListener('pageshow', queueUpdate, { signal: controller.signal });

      const cleanup = () => {
        controller.abort();
        window.cancelAnimationFrame(updateFrame);
        setVisible(false);
        delete stickyCta.dataset.stickyPageCtaInitialized;
        delete stickyCta.__ksStickyPageCtaCleanup;
      };

      stickyCta.__ksStickyPageCtaCleanup = cleanup;
      window.requestAnimationFrame(update);
    });
  }

  function initScope(scope = document) {
    initUpcartBridge(scope);
    initDialogs(scope);
    initHeaderMenuButtons(scope);
    initHeaderMenus(scope);
    initStandardMegaMenus(scope);
    initDrawerMenus(scope);
    initPredictiveSearch(scope);
    initQuickAdd(scope);
    initProductCardSwatches(scope);
    initDeferredImages(scope);
    initProductCardSecondaryImages(scope);
    initHorizontalProductImagePreloading(scope);
    initImageReveals(scope);
    initMediaFades(scope);
    initParallaxMedia(scope);
    initFloatingMediaGroupContainment(scope);
    initOffsetImageGroupContainment(scope);
    initAnimations(scope);
    initStickyHeaders(scope);
    initAutoSubmit(scope);
    initKlaviyoNewsletterForms(scope);
    initCustomerAddressForms(scope);
    initAjaxCart(scope);
    initCartDrawer(scope);
    initCartDrawerScrollbars(scope);
    initProductRecommendations(scope);
    initRecentlyViewedProducts(scope);
    initProductSections(scope);
    initCompleteSets(scope);
    initStickyPageCtas(scope);
    initRegisteredFeatures(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initScope(document);
    });
  } else {
    initScope(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initScope(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    cleanupParallaxMedia(event.target);
    cleanupMediaContainment(event.target);
    cleanupStickyPageCtas(event.target);
  });

  window.addEventListener('popstate', () => {
    const currentSection = document.querySelector('[data-product-section-id]');

    if (!currentSection || !window.history.state || window.history.state.ksCombinedProduct !== true) {
      return;
    }

    replaceProductSection(currentSection, window.location.href, {
      announceLabel: currentSection.dataset.productTitle || 'Product',
    }).catch(() => {
      window.location.reload();
    });
  });

  window.KSTheme = {
    buildStoreProductUrl,
    collect,
    escapeHtml,
    extractStoreProductImage,
    getLocaleRoot,
    normalizeStoreProductId,
    registerFeature,
    announce,
    fetchCart,
    formatMoney,
    init: initScope,
    updateCartCount,
  };

  document.dispatchEvent(new CustomEvent('ks:theme:ready', { detail: window.KSTheme }));
})();
